import axios from "axios";
import { store } from "../store";
import { setCredentials, logout } from "../store/slices/authSlice";
import { formatErrorMessage } from "../utils/errorFormatter";

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT;

const api = axios.create({
  baseURL: API_BASE_URL,
  // Send + receive cookies on cross-origin requests. Required for the
  // trusted-device login flow — the HttpOnly `trusted_device_token`
  // cookie has to flow back to the backend on every login attempt for
  // the OTP-skip fast path to recognise the browser.
  // Backend must set CORS_ALLOW_CREDENTIALS=True AND list our origin
  // explicitly (no wildcard) — handled in settings.
  withCredentials: true,
});

// (Removed in-memory cache/dedupe logic to avoid CORS/preflight complexities)

const __wrapWithAbort = (promise, signal) => {
  if (!signal) return promise;
  return new Promise((resolve, reject) => {
    let finished = false;
    const onAbort = () => {
      if (finished) return;
      const cancelErr = new Error("Request canceled");
      cancelErr.name = "CanceledError";
      reject(cancelErr);
    };
    if (signal.aborted) return onAbort();
    signal.addEventListener("abort", onAbort, { once: true });
    promise
      .then((v) => {
        finished = true;
        resolve(v);
      })
      .catch((e) => {
        finished = true;
        reject(e);
      })
      .finally(() => {
        signal.removeEventListener("abort", onAbort);
      });
  });
};

// Generic GET without client-side cache/dedupe (simplified to avoid CORS preflights)
const getWithCache = async (url, { params, signal } = {}) => {
  const reqPromise = api.get(url, { params, signal });
  return __wrapWithAbort(reqPromise, signal);
};

// Track active API requests to know when all have completed
let activeRequestCount = 0;
const API_IDLE_EVENT = "apiIdle";

const notifyIfIdle = () => {
  if (activeRequestCount === 0) {
    // Allow React render queue to flush before translating
    setTimeout(() => {
      window.dispatchEvent(new Event(API_IDLE_EVENT));
    }, 50);
  }
};

// Token refresh management
// This system automatically refreshes expired tokens and retries failed requests
// - isRefreshing: prevents multiple simultaneous refresh attempts
// - failedQueue: stores requests that failed during token refresh to retry them
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Helper function to check if token is expired or will expire soon (within 5 minutes)
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    const bufferTime = 5 * 60; // 5 minutes buffer

    return payload.exp < currentTime + bufferTime;
  } catch (error) {
    console.error("Error parsing token:", error);
    return true;
  }
};

const refreshToken = async () => {
  try {
    const storedTokens = localStorage.getItem("tokens");
    if (!storedTokens) {
      throw new Error("No refresh token available");
    }

    const tokens = JSON.parse(storedTokens);
    if (!tokens.refresh) {
      throw new Error("No refresh token available");
    }

    // Check if refresh token is expired
    if (isTokenExpired(tokens.refresh)) {
      throw new Error("Refresh token expired");
    }

    // Create a new axios instance without interceptors to avoid infinite loops
    const refreshApi = axios.create({
      baseURL: API_BASE_URL,
      // headers: {
      //   "Content-Type": "application/json",
      // },
    });

    const response = await refreshApi.post("/users/refresh-token/", {
      refresh: tokens.refresh,
    });

    if (response.data.success) {
      const newTokens = {
        access: response.data.access,
        refresh: response.data.refresh,
      };

      const userData = {
        user: {
          id: response.data.id,
          email: response.data.email,
          kycVerified: response.data.kyc_verified,
          streamChatId: response.data.stream_chat_id,
          steamVideoId: response.data.stream_video_token,
          roles: response.data.roles || [],
          userTypeSelected: response.data.user_type_selected,
          // Keep the superuser flag across token refreshes so the "View as"
          // control (TP-View-As) doesn't disappear mid-session.
          is_superuser: response.data.is_superuser === true,
        },
        tokens: newTokens,
      };

      // Enrich user with permissions/groups from profile API
      try {
        const profileRes = await refreshApi.get("/users/profile/", {
          headers: { Authorization: `Bearer ${newTokens.access}` },
        });
        const profile = profileRes?.data || {};
        const resolvedPermissions =
          profile.permissions ||
          profile?.data?.permissions ||
          profile?.results?.permissions ||
          [];
        const resolvedGroups =
          profile.groups ||
          profile?.data?.groups ||
          profile?.results?.groups ||
          [];
        const rolesFromProfile =
          profile.roles || profile?.data?.roles || profile?.results?.roles;

        if (Array.isArray(resolvedPermissions)) {
          userData.user.permissions = resolvedPermissions;
        }
        if (Array.isArray(resolvedGroups)) {
          userData.user.groups = resolvedGroups;
        }
        if (Array.isArray(rolesFromProfile) && rolesFromProfile.length > 0) {
          userData.user.roles = rolesFromProfile;
        }
        const superuserFromProfile =
          profile.is_superuser ??
          profile?.data?.is_superuser ??
          profile?.results?.is_superuser;
        if (typeof superuserFromProfile === "boolean") {
          userData.user.is_superuser = superuserFromProfile;
        }
      } catch (e) {
        // If profile fetch fails, proceed without permissions/groups
      }

      // Update localStorage and Redux store
      localStorage.setItem("tokens", JSON.stringify(newTokens));
      localStorage.setItem("user", JSON.stringify(userData.user));
      store.dispatch(setCredentials(userData));

      return newTokens.access;
    } else {
      throw new Error("Token refresh failed");
    }
  } catch (error) {
    console.error("Token refresh error:", error);
    // Clean up on refresh failure
    localStorage.removeItem("tokens");
    localStorage.removeItem("user");
    store.dispatch(logout());
    throw error;
  }
};

// Request interceptor
api.interceptors.request.use(
  async (config) => {
    // Optionally add a timestamp param to GET requests to avoid stale proxies, no custom headers
    if (String(config.method || "get").toLowerCase() === "get") {
      if (
        typeof URLSearchParams !== "undefined" &&
        config.params instanceof URLSearchParams
      ) {
        config.params.set("_ts", String(Date.now()));
      } else {
        const existingParams =
          config.params && typeof config.params === "object"
            ? config.params
            : {};
        config.params = { ...existingParams, _ts: Date.now() };
      }
    }
    // Don't check auth for auth-related endpoints and signup endpoints
    const isPublicEndpoint =
      config.url.includes("/users/login/") ||
      config.url.includes("/users/2factor/") ||
      config.url.includes("/users/otp-send/") ||
      config.url.includes("/users/otp-verify/") ||
      config.url.includes("/users/otp-resend-email/") ||
      config.url.includes("/users/otp-resend-email-signup/") ||
      config.url.includes("/users/registration/") ||
      config.url.includes("/users/refresh-token/") ||
      config.url.includes("/users/password_reset/") ||
      config.url.includes("/users/password_reset_confirm/") ||
      config.url.includes("/dashboard/countries/") ||
      config.url.includes("/dashboard/clubs/public/") ||
      config.url.includes("/dashboard/players/public/") ||
      config.url.includes("/dashboard/transfermarkt/transfers/") ||
      config.url.includes("/dashboard/transfermarkt/rumors/");

    if (isPublicEndpoint) {
      activeRequestCount += 1;
      return config;
    }

    const state = store.getState();
    const auth = state.auth;

    // Check if we need to refresh the token proactively
    if (auth?.tokens?.access && isTokenExpired(auth.tokens.access)) {
      try {
        const newAccessToken = await refreshToken();
        config.headers.Authorization = `Bearer ${newAccessToken}`;
        activeRequestCount += 1;
        return config;
      } catch (error) {
        console.error("Proactive token refresh failed:", error);
        // Continue with expired token, let response interceptor handle 401
      }
    }

    // If no access token, try to refresh before failing
    if (!auth?.tokens?.access) {
      const storedTokens = localStorage.getItem("tokens");
      if (storedTokens) {
        try {
          const tokens = JSON.parse(storedTokens);
          if (tokens.refresh) {
            // Attempt to refresh token
            const newAccessToken = await refreshToken();
            config.headers.Authorization = `Bearer ${newAccessToken}`;
            activeRequestCount += 1;
            return config;
          }
        } catch (error) {
          console.error("Token refresh failed in request interceptor:", error);
          // Token refresh failed, let the request proceed without auth
          // The response interceptor will handle the 401 error
        }
      }

      // No valid tokens available - let request proceed and handle 401 in response interceptor
      activeRequestCount += 1;
      return config;
    }

    // Add token to request if available
    if (auth?.tokens?.access) {
      config.headers.Authorization = `Bearer ${auth.tokens.access}`;
    }

    activeRequestCount += 1;
    return config;
  },
  (error) => {
    activeRequestCount = Math.max(activeRequestCount - 1, 0);
    notifyIfIdle();
    return Promise.reject(error);
  },
);

// Permission error handler
const handlePermissionError = (error) => {
  if (error.response?.status === 403) {
    // Opt-out for callers that render the denial themselves (QAF01-056: the
    // organizer directory is platform-staff-only by design, so its 403 is an
    // expected gate, not an incident — the page explains it in place). Purely
    // additive: without the flag on the request config nothing changes for any
    // existing caller. Mirrors how MessageRequestsPanel.js:238-243 treats an
    // expected 403 as a gate instead of a red banner.
    if (error.config?.suppressPermissionToast) return;
    const errorData = error.response?.data;
    const requiredPermission = errorData?.required_permission;
    const detail = errorData?.detail;

    // Import toast dynamically to avoid circular dependencies
    import("react-hot-toast").then(({ default: toast }) => {
      if (requiredPermission) {
        toast.error(
          `Access denied. Required permission: ${requiredPermission}`,
        );
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error("You do not have permission to perform this action.");
      }
    });
  }
};

// Response interceptor
api.interceptors.response.use(
  (response) => {
    activeRequestCount = Math.max(activeRequestCount - 1, 0);
    notifyIfIdle();
    // On successful mutations we previously cleared client cache; no-op now.
    return response;
  },
  async (error) => {
    activeRequestCount = Math.max(activeRequestCount - 1, 0);
    notifyIfIdle();

    // Handle permission errors
    handlePermissionError(error);

    const originalRequest = error.config;

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for public endpoints and refresh endpoint itself
      const isPublicEndpoint =
        originalRequest.url.includes("/users/login/") ||
        originalRequest.url.includes("/users/2factor/") ||
        originalRequest.url.includes("/users/otp-send/") ||
        originalRequest.url.includes("/users/otp-verify/") ||
        originalRequest.url.includes("/users/otp-resend-email-signup/") ||
        originalRequest.url.includes("/users/registration/") ||
        originalRequest.url.includes("/users/refresh-token/") ||
        originalRequest.url.includes("/users/password_reset/") ||
        originalRequest.url.includes("/users/password_reset_confirm/") ||
        originalRequest.url.includes("/dashboard/countries/") ||
        originalRequest.url.includes("/dashboard/clubs/public/") ||
        originalRequest.url.includes("/dashboard/players/public/") ||
        originalRequest.url.includes("/dashboard/transfermarkt/transfers/") ||
        originalRequest.url.includes("/dashboard/transfermarkt/rumors/");

      if (isPublicEndpoint) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshToken();
        processQueue(null, newAccessToken);

        // Retry the original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Refresh failed - clean up auth state
        localStorage.removeItem("tokens");
        localStorage.removeItem("user");
        store.dispatch(logout());

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

// Shared post-auth handler — turns a backend login payload (issued either
// by the OTP verify endpoint OR by the trusted-device fast-path on the
// login endpoint) into a Redux-ready userData shape, enriches it from
// /users/profile/, persists to localStorage and dispatches setCredentials.
// Returns the same shape as verify2FA used to return so callers don't
// need branching.
const _handleAuthSuccessPayload = async (responseData) => {
  const userData = {
    user: {
      id: responseData.id,
      email: responseData.email,
      kycVerified: responseData.kyc_verified,
      streamChatId: responseData.stream_chat_id,
      steamVideoId: responseData.stream_video_token,
      roles: responseData.roles || [],
      userTypeSelected: responseData.user_type_selected,
      // Superuser flag drives the "View as" role preview control (TP-View-As).
      // The real gate is the backend's IsSuperuser 403 — this is UX only.
      is_superuser: responseData.is_superuser === true,
    },
    tokens: {
      access: responseData.access,
      refresh: responseData.refresh,
    },
  };

  // Save tokens first so the subsequent /users/profile/ call carries auth.
  localStorage.setItem("tokens", JSON.stringify(userData.tokens));

  // Enrich with permissions + groups from /profile (best-effort).
  try {
    const profileRes = await api.get("/users/profile/");
    const profile = profileRes?.data || {};
    const resolvedPermissions =
      profile.permissions ||
      profile?.data?.permissions ||
      profile?.results?.permissions ||
      [];
    const resolvedGroups =
      profile.groups ||
      profile?.data?.groups ||
      profile?.results?.groups ||
      [];
    const rolesFromProfile =
      profile.roles || profile?.data?.roles || profile?.results?.roles;

    if (Array.isArray(resolvedPermissions)) userData.user.permissions = resolvedPermissions;
    if (Array.isArray(resolvedGroups)) userData.user.groups = resolvedGroups;
    if (Array.isArray(rolesFromProfile) && rolesFromProfile.length > 0) {
      userData.user.roles = rolesFromProfile;
    }
    const superuserFromProfile =
      profile.is_superuser ??
      profile?.data?.is_superuser ??
      profile?.results?.is_superuser;
    if (typeof superuserFromProfile === "boolean") {
      userData.user.is_superuser = superuserFromProfile;
    }

    // Display names (T012): the login payload carries no names, so this
    // profile fetch is the only auth-time source. Copy them onto the Redux
    // user so attribution lines ("Posting as {club}", "Sent as {agent} ·
    // {agency}") render real names instead of their fallback literals.
    const nameFromProfile =
      profile.name ||
      profile?.data?.name ||
      profile?.results?.name ||
      profile?.user_details?.name;
    const clubNameFromProfile =
      profile.club_details?.name ||
      profile?.data?.club_details?.name ||
      profile?.results?.club_details?.name;
    const agencyNameFromProfile =
      profile.agency_details?.name ||
      profile?.data?.agency_details?.name ||
      profile?.results?.agency_details?.name;
    if (nameFromProfile) userData.user.name = nameFromProfile;
    if (clubNameFromProfile) userData.user.club_name = clubNameFromProfile;
    if (agencyNameFromProfile) userData.user.agency_name = agencyNameFromProfile;
  } catch (e) {
    // Proceed without permissions enrichment if profile fetch fails — the
    // user is still authenticated, just without granular perms loaded.
  }

  localStorage.setItem("user", JSON.stringify(userData.user));
  store.dispatch(setCredentials(userData));

  return userData;
};

const authService = {
  // Step 1: email + password. Two possible responses from the backend:
  //   - otp_required: true  → OTP was sent, frontend should switch to the
  //                            2fa step and call verify2FA on submit
  //   - otp_required: false → fast path. The browser had a valid
  //                            trusted_device cookie + the response already
  //                            includes JWT access + refresh tokens. We do
  //                            the same post-auth handling as verify2FA
  //                            and tell the caller "no OTP needed".
  login: async ({ email, password, invite_token }) => {
    try {
      const payload = { email, password };
      if (invite_token) {
        payload.invite_token = invite_token;
      }

      const response = await api.post("/users/login/", payload);
      const data = response.data || {};

      // Fast path: trusted-device skip. Backend already issued tokens.
      if (data.otp_required === false && data.access && data.refresh) {
        const userData = await _handleAuthSuccessPayload(data);
        return {
          success: true,
          otp_required: false,
          data: userData,
        };
      }

      // Standard path: OTP was sent, caller must show the 2fa UI.
      return {
        success: true,
        otp_required: true,
        data,
      };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to login",
      };
    }
  },

  // Forgot Password
  forgotPassword: async ({ email }) => {
    try {
      const response = await api.post("/users/password_reset/", {
        email,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Forgot password error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to send reset email",
      };
    }
  },

  // Reset Password (handles both authenticated and unauthenticated modes)
  resetPassword: async ({ uid, token, new_password }) => {
    try {
      const payload = { new_password };

      // Add uid and token if provided (unauthenticated mode)
      if (uid && token) {
        payload.uid = uid;
        payload.token = token;
      }

      const response = await api.post(
        "/users/password_reset_confirm/",
        payload,
      );

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Reset password error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to reset password",
      };
    }
  },

  // Change Password (authenticated users with old password)
  changePassword: async ({ old_password, new_password }) => {
    try {
      const response = await api.patch("/users/change_password/", {
        old_password,
        new_password,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Change password error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to change password",
      };
    }
  },

  // Send OTP
  sendOTP: async ({ email, phoneNumber, invite_token }) => {
    try {
      // Callers pass snake_case `invite_token` (same convention as login and
      // register above). A camelCase mismatch here used to drop the token from
      // the request body entirely (QAF01-066).
      const response = await api.post("/users/otp-send/", {
        email,
        mobile: phoneNumber,
        invite_token: invite_token || null,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to send OTP",
      };
    }
  },

  // Verify OTP
  verifyOTP: async ({ otp, phoneNumber }) => {
    try {
      const response = await api.post("/users/otp-verify/", {
        otp,
        mobile: phoneNumber,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to verify OTP",
      };
    }
  },

  // Register User
  register: async ({ email, phoneNumber, password, invite_token }) => {
    try {
      const payload = {
        email,
        mobile: phoneNumber,
        password,
      };

      // Add invite_token if provided
      if (invite_token) {
        payload.invite_token = invite_token;
      }

      const response = await api.post("/users/registration/", payload);

      if (response.data.success) {
        const userData = {
          user: {
            id: response.data.id,
            email: response.data.email,
            kycVerified: response.data.kyc_verified || false,
            streamChatId: response.data.stream_chat_id,
            steamVideoId: response.data.stream_video_token,
            roles: response.data.roles || [],
            userTypeSelected: false,
          },
          tokens: {
            access: response.data.access,
            refresh: response.data.refresh,
          },
        };

        // Save tokens to localStorage first so we can call profile
        localStorage.setItem("tokens", JSON.stringify(userData.tokens));

        store.dispatch(setCredentials(userData));

        return {
          success: true,
          data: response.data,
        };
      }

      throw new Error("Invalid response format");
    } catch (error) {
      console.error("Registration error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to register user",
      };
    }
  },

  // Logout
  logout: async () => {
    try {
      await api.post("/users/logout/");
      localStorage.removeItem("tokens");
      store.dispatch(logout());
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to logout",
      };
    }
  },

  // Verify 2FA
  // Email-channel OTP fallback. Used when SMS doesn't deliver.
  // Triggers Twilio Verify with channel='email' via the existing SendGrid integration.
  resendOtpByEmail: async ({ email }) => {
    try {
      const response = await api.post("/users/otp-resend-email/", { email });
      if (response?.data?.success) {
        return { success: true, message: response.data.message };
      }
      return {
        success: false,
        error: response?.data?.message || "Failed to resend OTP via email",
      };
    } catch (err) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Failed to resend OTP via email. Please try again.",
      };
    }
  },

  // Email-channel OTP fallback for the SIGNUP flow (no account exists yet).
  // Mirrors resendOtpByEmail but hits the onboarding-specific endpoint, which
  // keys cache by mobile (the verify endpoint only receives mobile).
  resendSignupOtpByEmail: async ({ email, phoneNumber }) => {
    try {
      const response = await api.post("/users/otp-resend-email-signup/", {
        email,
        mobile: phoneNumber,
      });
      if (response?.data?.success) {
        return { success: true, message: response.data.message };
      }
      return {
        success: false,
        error: response?.data?.message || "Failed to resend OTP via email",
      };
    } catch (err) {
      return {
        success: false,
        error:
          err?.response?.data?.message ||
          "Failed to resend OTP via email. Please try again.",
      };
    }
  },

  verify2FA: async ({ email, code, invite_token, trust_device }) => {
    try {
      const payload = { email, otp: code };
      if (invite_token) {
        payload.invite_token = invite_token;
      }
      // Opt-in: backend creates a TrustedDevice + HttpOnly cookie when
      // this flag is true. Cookie travels because api has
      // withCredentials:true. UI sets this from a checkbox under the OTP.
      if (trust_device) {
        payload.trust_device = true;
      }
      const response = await api.post("/users/2factor/", payload);

      if (response.data) {
        const userData = await _handleAuthSuccessPayload(response.data);
        return {
          success: true,
          data: userData,
        };
      }

      return {
        success: false,
        error: "Invalid response from server",
      };
    } catch (error) {
      console.error("2FA verification error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to verify code",
      };
    }
  },

  // Updated createClub to use api instance
  createClub: async (data) => {
    try {
      const response = await api.post("/users/create-club/", data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create club",
      };
    }
  },

  // Create agency
  createAgency: async (data) => {
    try {
      const formData = new FormData();
      formData.append("agency_name", data.agency_name);
      formData.append("admin_name", data.admin_name);
      formData.append("agent_license_number", data.agent_license_number);

      // Only append logo if it exists
      if (data.logo) {
        formData.append("profile_picture", data.logo);
      }

      // Only append the license document if it exists.
      // IMPORTANT: the field name must NOT be "document" — the create-agency
      // endpoint reads `request.FILES.get('profile_picture') or
      // request.FILES.get('document')` for the agency LOGO, so a file named
      // "document" would land in the logo slot. The agent path keeps its own
      // "document" name; this path uses "license_document".
      if (data.licenseFile) {
        formData.append("license_document", data.licenseFile);
      }

      const response = await api.post("/users/create-agency/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Create agency error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create agency",
      };
    }
  },

  // Create agent
  createAgent: async (data) => {
    try {
      // Check if we have a license file to upload
      if (data.licenseFile) {
        // Use FormData for multipart/form-data when license file is present
        const formData = new FormData();
        formData.append("full_name", data.full_name);

        // Only append license_number if it exists
        if (data.license_number) {
          formData.append("license_number", data.license_number);
        }

        // Only append agency_id if it exists
        if (data.agency_id) {
          formData.append("agency_id", data.agency_id);
        }

        // Append the license document
        formData.append("document", data.licenseFile);

        const response = await api.post("/users/create-agent/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        return {
          success: true,
          data: response.data,
        };
      } else {
        // Use regular JSON for requests without file upload
        const response = await api.post("/users/create-agent/", data);
        return {
          success: true,
          data: response.data,
        };
      }
    } catch (error) {
      console.error("Create agent error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create agent",
      };
    }
  },

  // Update agent license
  updateAgentLicense: async (data) => {
    try {
      // Check if we have a document file to upload
      if (data.document) {
        // Use FormData for multipart/form-data when document is present
        const formData = new FormData();

        // Only append license_number if it exists
        if (data.license_number) {
          formData.append("license_number", data.license_number);
        }

        // Only append license_organization_name if it exists
        if (data.license_organization_name) {
          formData.append(
            "license_organization_name",
            data.license_organization_name,
          );
        }

        // Append the license document
        formData.append("document", data.document);

        const response = await api.post(
          "/users/update-agent-license/",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );
        return {
          success: true,
          data: response.data,
        };
      } else {
        // Use regular JSON for requests without file upload
        const response = await api.post("/users/update-agent-license/", data);
        return {
          success: true,
          data: response.data,
        };
      }
    } catch (error) {
      console.error("Update agent license error:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to update agent license",
      };
    }
  },

  // Create player
  createPlayer: async (data) => {
    try {
      const response = await api.post("/users/create-player/", data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Create player error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create player",
      };
    }
  },

  // Create staff
  createStaff: async (data) => {
    try {
      const response = await api.post("/users/create-staff/", data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Create staff error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create staff",
      };
    }
  },

  // Create organizer
  createOrganizer: async (data) => {
    try {
      const response = await api.post("/users/create-organizer/", data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Create organizer error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create organizer",
      };
    }
  },

  // Create lawyer
  createLawyer: async (data) => {
    try {
      const response = await api.post("/users/create-lawyer/", data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Create lawyer error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create lawyer",
      };
    }
  },

  // Create coach
  createCoach: async (data) => {
    try {
      const response = await api.post("/users/create-coach/", data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Create coach error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create coach",
      };
    }
  },

  // Signature APIs
  uploadSignature: async (formData) => {
    try {
      const response = await api.post(
        "/dashboard/signatures/upload/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Signature upload error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to upload signature",
      };
    }
  },

  getSignature: async () => {
    try {
      const response = await api.get("/dashboard/signatures/get_signature/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Get signature error:", error);
      // It's okay if a signature doesn't exist, so handle 404 gracefully
      if (error.response?.status === 404) {
        return {
          success: true,
          data: { signature_image_s3_url: null },
        };
      }
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch signature",
      };
    }
  },

  deleteSignature: async () => {
    try {
      await api.delete("/dashboard/signatures/delete/");
      return {
        success: true,
      };
    } catch (error) {
      console.error("Delete signature error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to delete signature",
      };
    }
  },
  // Refresh tokens manually
  refreshTokens: async () => {
    try {
      const newAccessToken = await refreshToken();
      return {
        success: true,
        accessToken: newAccessToken,
      };
    } catch (error) {
      console.error("Manual token refresh error:", error);
      return {
        success: false,
        error: error?.message || "Failed to refresh tokens",
      };
    }
  },

  // End Signature APIs
};

// Trusted-device management — list / revoke / revoke-all. Used by the
// Settings → Security page. Requires the user to be authenticated; the
// regular Bearer token interceptor handles that.
const trustedDeviceService = {
  list: async ({ signal } = {}) => {
    try {
      const response = await api.get("/users/trusted-devices/", { signal });
      return { success: true, data: response.data?.devices || [] };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to load trusted devices.",
      };
    }
  },

  revoke: async (deviceId) => {
    try {
      const response = await api.delete(`/users/trusted-devices/${deviceId}/`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to revoke device.",
      };
    }
  },

  revokeAll: async () => {
    try {
      const response = await api.post("/users/trusted-devices/revoke-all/");
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to revoke all devices.",
      };
    }
  },
};

// Banned Agents (club settings). The silent ban: the club bans an agent from
// ONE request or for the running transfer window, and the agent simply stops
// seeing those requests. Nothing here notifies the agent — that is the point,
// and it is enforced on the backend (no notify() in the ban endpoints at all).
const bannedAgentService = {
  list: async ({ signal } = {}) => {
    try {
      const response = await api.get("/dashboard/club/banned-agents/", { signal });
      return { success: true, data: response.data?.results || [] };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to load banned agents.",
      };
    }
  },

  // scope: "request" | "transfer_window". Either pitch_id, or
  // agent_profile_id + tender_id.
  ban: async (payload) => {
    try {
      const response = await api.post("/dashboard/club/banned-agents/", payload);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to ban this agent.",
      };
    }
  },

  unban: async (banId) => {
    try {
      const response = await api.post(
        `/dashboard/club/banned-agents/${banId}/unban/`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to lift this ban.",
      };
    }
  },
};

const userService = {
  getProfile: async () => {
    try {
      const response = await api.get("/users/profile/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch profile",
      };
    }
  },

  // Player Profile APIs
  getPlayerProfile: async () => {
    try {
      const response = await api.get("/users/profile/player/");

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch player profile",
      };
    }
  },

  updatePlayerProfile: async (data) => {
    try {
      // Check if data is FormData or plain object
      let config = {};
      if (data instanceof FormData) {
        config = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
      }

      const response = await api.put("/users/profile/player/", data, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to update player profile",
      };
    }
  },

  // Agent Profile APIs
  getAgentProfile: async () => {
    try {
      const response = await api.get("/users/profile/agent/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch agent profile",
      };
    }
  },

  updateAgentProfile: async (data) => {
    try {
      // Check if data is FormData or plain object
      let config = {};
      if (data instanceof FormData) {
        config = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
      }

      const response = await api.put("/users/profile/agent/", data, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to update agent profile",
      };
    }
  },

  // Club Admin Profile APIs
  getClubAdminProfile: async () => {
    try {
      const response = await api.get("/users/profile/club-admin/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch club admin profile",
      };
    }
  },

  updateClubAdminProfile: async (data) => {
    try {
      // Use FormData for multipart/form-data
      let config = {};
      if (data instanceof FormData) {
        config = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
      }

      const response = await api.put(
        "/users/profile/club-admin/",
        data,
        config,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to update club admin profile",
      };
    }
  },

  // Coach Profile APIs
  getCoachProfile: async () => {
    try {
      const response = await api.get("/users/profile/coach/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch coach profile",
      };
    }
  },

  updateCoachProfile: async (data, config = {}) => {
    try {
      // If config is not provided, determine it based on data type
      let requestConfig = config;
      if (!config.headers) {
        if (data instanceof FormData) {
          requestConfig = {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          };
        } else {
          requestConfig = {
            headers: {
              "Content-Type": "application/json",
            },
          };
        }
      }

      const response = await api.put(
        "/users/profile/coach/",
        data,
        requestConfig,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      // DRF answers a rejected field with {"<field>": ["message"]} and no
      // `message` key, so a plain fallback would hide WHY the save failed.
      // Hand the field errors back so the form can show them on the field.
      const data = error?.response?.data;
      const fieldErrors =
        data && typeof data === "object" && !Array.isArray(data) ? data : null;
      const firstFieldMessage = fieldErrors
        ? Object.values(fieldErrors)
            .flat()
            .find((m) => typeof m === "string")
        : null;
      return {
        success: false,
        error:
          data?.message ||
          data?.detail ||
          firstFieldMessage ||
          "Failed to update coach profile",
        fieldErrors,
      };
    }
  },

  sendMobileChangeOtp: async (payload) => {
    try {
      const response = await api.post(
        "/users/profile/mobile/change/send-otp/",
        payload,
      );
      return {
        success: true,
        data: response.data,
        message: response.data?.message,
      };
    } catch (error) {
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to send verification code",
        error: error?.response?.data,
      };
    }
  },

  verifyMobileChangeOtp: async (payload) => {
    try {
      const response = await api.post(
        "/users/profile/mobile/change/verify/",
        payload,
      );
      return {
        success: true,
        data: response.data,
        message: response.data?.message,
      };
    } catch (error) {
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to verify mobile number",
        error: error?.response?.data,
      };
    }
  },

  uploadClubLogo: async (formData) => {
    try {
      const response = await api.post(
        "/users/profile/club-admin/logo/upload/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || "Logo upload failed",
      };
    }
  },

  getProfileBusinessCard: async () => {
    try {
      const response = await api.get("/users/business-cards");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch business card",
      };
    }
  },

  updateProfileBusinessCard: async (data) => {
    try {
      const response = await api.post("/users/business-cards", data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to update business card",
      };
    }
  },

  getStaffProfile: async () => {
    try {
      const response = await api.get("/users/profile/staff/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch staff profile",
      };
    }
  },

  // Public Staff Profile API (read-only)
  getPublicStaffProfile: async (accountId) => {
    try {
      const response = await api.get(
        `/users/public/profile/staff/${accountId}/`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch public staff profile",
      };
    }
  },

  updateStaffProfile: async (data) => {
    try {
      // Check if data is FormData or plain object
      let config = {};
      if (data instanceof FormData) {
        config = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
      }

      const response = await api.put("/users/profile/staff/", data, config);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      // DRF answers a rejected field with {"<field>": ["message"]} and no
      // `message` key, so a plain fallback would hide WHY the save failed —
      // the user was left with "Failed to update staff profile" and no idea
      // that it was the date of birth. Hand the field errors back so the form
      // can show them on the field, and lift the first one into the banner.
      const data_ = error?.response?.data;
      const fieldErrors =
        data_ && typeof data_ === "object" && !Array.isArray(data_)
          ? data_
          : null;
      const firstFieldMessage = fieldErrors
        ? Object.values(fieldErrors)
            .flat()
            .find((m) => typeof m === "string")
        : null;
      return {
        success: false,
        error:
          data_?.message ||
          data_?.detail ||
          firstFieldMessage ||
          "Failed to update staff profile",
        fieldErrors,
      };
    }
  },

  // Staff Documents APIs
  getStaffDocuments: async () => {
    try {
      const response = await api.get("/users/profile/staff/documents/");
      // Support both wrapped and direct responses
      const data =
        response.data?.data || response.data?.results || response.data || [];
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch staff documents",
      };
    }
  },
  createStaffDocument: async (formData) => {
    try {
      const response = await api.post(
        "/users/profile/staff/documents/",
        formData,
      );
      const data = response.data?.data || response.data;
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to create staff document",
      };
    }
  },
  updateStaffDocument: async (id, payload) => {
    try {
      const response = await api.patch(
        `/users/profile/staff/documents/${id}/`,
        payload,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to update staff document",
      };
    }
  },

  // Set document as primary profile photo (from MD specification)
  setStaffDocumentAsPrimary: async (documentId) => {
    try {
      const response = await api.patch(
        `/users/profile/staff/documents/${documentId}/`,
        { is_primary: true },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to set document as primary",
      };
    }
  },
  deleteStaffDocument: async (id) => {
    try {
      await api.delete(`/users/profile/staff/documents/${id}/`);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to delete staff document",
      };
    }
  },
};

const dashboardService = {
  getTopLeagueStandings: async ({ signal } = {}) => {
    try {
      const response = await api.get("/api/standings/top-leagues/", { signal });
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch standings",
      };
    }
  },

  // Club-Matches-Tab (Build-Brief 2026-08-20): club schedule across all
  // competitions — BE contract #436/#438/#439.
  getClubMatches: async (clubId, { signal } = {}) => {
    try {
      const response = await api.get(
        `/api/clubs/${encodeURIComponent(clubId)}/matches/`,
        { signal },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch club matches",
      };
    }
  },

  getSeasonStandings: async (seasonId, { signal } = {}) => {
    try {
      const response = await api.get(
        `/api/standings/${encodeURIComponent(seasonId)}/`,
        { signal },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch standings",
      };
    }
  },

  getAvailableLeagues: async ({ signal } = {}) => {
    try {
      const response = await api.get("/api/standings/leagues/", { signal });
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch leagues",
      };
    }
  },

  getLiveMatches: async (competitionId, { signal } = {}) => {
    try {
      const response = await api.get(
        `/api/standings/live-matches/${encodeURIComponent(competitionId)}/`,
        { signal },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch live matches",
      };
    }
  },

  // ── Cup (knockout bracket) widget ──────────────────────────────────────────
  getAvailableCups: async ({ signal } = {}) => {
    try {
      const response = await api.get("/api/cups/", { signal });
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch cups",
      };
    }
  },

  // seasonId (optional, additive — Competitions build): view a past edition;
  // omitted = current season, exactly the widget's existing behaviour.
  getCupBracket: async (competitionId, { seasonId, signal } = {}) => {
    try {
      const response = await api.get(
        `/api/cups/${encodeURIComponent(competitionId)}/`,
        { params: seasonId ? { season_id: seasonId } : undefined, signal },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch cup bracket",
      };
    }
  },

  // ── Competitions (TP-CompetitionPage build) ────────────────────────────────
  // URLs = the merged BE V1 API (views_competitions.py):
  //   GET /api/competitions/                       browse (peek-paginated)
  //   GET /api/competitions/<id>/                  detail/hero + seasons
  //   GET /api/competitions/<id>/matches/          ?season_id= (stage/round grouped)
  //   GET /api/competitions/<id>/leaderboards/     ?season_id=&limit=10|25
  // Standings/Groups reuse GET /api/standings/<season_id>/ (getSeasonStandings),
  // bracket reuses GET /api/cups/<id>/?season_id= (getCupBracket below).
  getCompetitionsBrowse: async ({ page, perPage, signal } = {}) => {
    try {
      const response = await api.get("/api/competitions/", {
        params: {
          ...(page ? { page } : {}),
          ...(perPage ? { per_page: perPage } : {}),
        },
        signal,
      });
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch competitions",
      };
    }
  },

  getCompetitionDetail: async (competitionId, { signal } = {}) => {
    try {
      const response = await api.get(
        `/api/competitions/${encodeURIComponent(competitionId)}/`,
        { signal },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch competition",
      };
    }
  },

  getCompetitionMatches: async (competitionId, { seasonId, signal } = {}) => {
    try {
      const response = await api.get(
        `/api/competitions/${encodeURIComponent(competitionId)}/matches/`,
        { params: seasonId ? { season_id: seasonId } : undefined, signal },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch matches",
      };
    }
  },

  getCompetitionLeaderboards: async (
    competitionId,
    { seasonId, limit, signal } = {},
  ) => {
    try {
      const response = await api.get(
        `/api/competitions/${encodeURIComponent(competitionId)}/leaderboards/`,
        {
          params: {
            ...(seasonId ? { season_id: seasonId } : {}),
            ...(limit ? { limit } : {}),
          },
          signal,
        },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch leaderboards",
      };
    }
  },

  getArticleById: async (articleId, { signal } = {}) => {
    try {
      const response = await api.get(
        `/api/news/article/${encodeURIComponent(articleId)}/`,
        { signal },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        status: error?.response?.status,
        error:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to load article",
      };
    }
  },

  getRelatedArticles: async (articleId, { signal, language } = {}) => {
    try {
      const params = {};
      if (language) params.language = language;
      const response = await api.get(
        `/api/news/article/${encodeURIComponent(articleId)}/related/`,
        { signal, params },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        status: error?.response?.status,
        error:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to load related articles",
      };
    }
  },

  getNewsEverything: async ({ signal, language, page, perPage } = {}) => {
    try {
      const params = {};
      if (language) params.language = language;
      if (page) params.page = page;
      if (perPage) params.per_page = perPage;
      const response = await api.get("/api/news/everything/", { signal, params });
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        status: error?.response?.status,
        error:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to load news",
      };
    }
  },

  getNewsTopHeadlines: async ({ signal, language, page, perPage } = {}) => {
    try {
      const params = {};
      if (language) params.language = language;
      if (page) params.page = page;
      if (perPage) params.per_page = perPage;
      const response = await api.get("/api/news/top-headlines/", { signal, params });
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        status: error?.response?.status,
        error:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to load top headlines",
      };
    }
  },

  getLatestNews: async (limit = 10, offset = 0, language = null) => {
    try {
      const params = { limit, offset };
      if (language) {
        params.language = language;
      }
      const response = await api.get("/dashboard/news/", {
        params,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch news",
      };
    }
  },

  getClubLatestTender: async () => {
    try {
      const response = await api.get("/dashboard/club/tender/latest/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch requests",
      };
    }
  },

  getLeaderboard: async () => {
    try {
      const response = await api.get("/users/tp-leader-board/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch leaderboard",
      };
    }
  },
};

const searchService = {
  search: async (params, signal) => {
    try {
      if (params.type === "coaches") {
        const response = await api.get("/dashboard/coaches/search/", {
          params: { q: params.query },
          signal,
        });
        return {
          success: true,
          data: {
            results: {
              coaches: response.data.results,
            },
          },
        };
      }

      // Cache by q/type/page locally for 30s, dedupe in-flight
      const response = await getWithCache("/dashboard/search/", {
        params,
        signal,
        cacheMaxAgeMs: 30_000,
        dedupe: true,
      });

      return {
        success: true,
        data: response.data,
        pagination: response.data.pagination,
      };
    } catch (error) {
      console.error("Search API Error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Search failed",
      };
    }
  },

  // Search agencies specifically for partner invitations
  searchAgencies: async (query, context = "partners") => {
    try {
      const params = {
        type: "agencies",
        query,
        context,
        page: 1,
        per_page: 25,
      };

      const response = await api.get("/dashboard/search/", { params });

      return {
        success: true,
        data: response.data.results?.agencies || [],
        pagination: response.data.pagination,
      };
    } catch (error) {
      console.error("Agency Search API Error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Agency search failed",
      };
    }
  },

  getCountries: async (search = "") => {
    try {
      const response = await api.get("/dashboard/countries/", {
        params: { search },
      });
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch countries",
      };
    }
  },

  getPositions: async () => {
    try {
      const response = await api.get("/dashboard/search/positions/");
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch positions",
      };
    }
  },

  // Fetch leagues – supports optional area filter (using country alpha3code)
  getLeagues: async (area = "") => {
    try {
      // Handle both direct area parameter and area=XYZ format
      let params = {};
      if (area) {
        if (area.startsWith("area=")) {
          params.area = area.substring(5);
        } else {
          params.area = area;
        }
      }

      const response = await api.get("/dashboard/search/leagues", { params });
      return {
        success: true,
        data: Array.isArray(response.data)
          ? response.data
          : response.data.results || response.data,
      };
    } catch (error) {
      console.log(error, "this is error");
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch leagues",
      };
    }
  },
};

// Request drafts — the backend's own words, not ours.
//
// The two dedicated draft actions answer a refusal as a DRF body that carries
// no `message` key at all: `{"detail": "This request is not a draft."}` for the
// lifecycle and the permission refusals, `{"scheduled_publish_at": "..."}` and
// `{"publish_mode": "Only a draft can be published or scheduled."}` for the
// validation ones — sometimes a bare string, sometimes DRF's list of one. Read
// against `data.message` (the shape the rest of this file grew up with) every
// one of them collapses into a generic "Failed to …", and the club is told
// nothing about WHY its click did not work.
//
// So: take the first real sentence the server sent, verbatim, without a
// `field:` prefix — these are one-action toasts, not a form with fields to
// point at. The generic string stays as the last resort for a network error,
// where there is no server sentence to show.
const draftActionError = (error, fallback) => {
  const data = error?.response?.data;
  // Objects only. A 500 answers with an HTML page, and a whole HTML page in a
  // toast is worse than the generic sentence.
  if (!data || typeof data !== "object" || Array.isArray(data)) return fallback;

  const firstSentence = (value) => {
    if (typeof value === "string" && value.trim()) return value;
    if (Array.isArray(value)) {
      const hit = value.find((v) => typeof v === "string" && v.trim());
      if (hit) return hit;
    }
    return null;
  };

  // `detail` first: that is where the lifecycle/permission refusals live.
  const detail = firstSentence(data.detail);
  if (detail) return detail;
  const message = firstSentence(data.message);
  if (message) return message;
  for (const value of Object.values(data)) {
    const sentence = firstSentence(value);
    if (sentence) return sentence;
  }
  return fallback;
};

const tenderService = {
  // Get all tenders (for club view)
  getAllTenders: async (params, signal) => {
    try {
      const actualParams =
        params instanceof URLSearchParams
          ? params
          : new URLSearchParams(params);
      if (!actualParams.has("view")) actualParams.append("view", "all");

      const response = await api.get("/dashboard/tenders/", {
        params: actualParams,
        signal,
      });

      return {
        success: true,
        data: response.data.results || [],
        pagination: {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: parseInt(actualParams.get("page") || "1"),
        },
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        throw error;
      }
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch all requests",
      };
    }
  },

  getSingleTender: async (tenderId) => {
    try {
      const response = await api.get(`/dashboard/tenders/${tenderId}/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch single request",
      };
    }
  },

  getTenderDivisions: async () => {
    try {
      const response = await api.get(`/dashboard/tenders/divisions/`);
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      return { success: true, data: list };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch divisions",
      };
    }
  },

  // Consolidated filter-options endpoint used by the tenders filter modal.
  // Returns countries / divisions / positions / continents that actually
  // appear on *active* tenders, with per-option tender counts. Supports
  // cascade filtering: passing `country` narrows the divisions+positions
  // lists, passing `continent` narrows everything to that continent.
  //
  // Replaces the older `searchService.getCountries` + `getTenderDivisions`
  // pair for callers that want counts + dynamic filtering.
  //
  // `surface` (DECISION 050) tells the backend WHICH list the counts belong to,
  // because one endpoint feeds two filter modals. Pass "classic" from the
  // classic club-request list — its counts then exclude the agent-marketplace
  // rows the list itself hides, so an option can no longer read "(1)" and then
  // return nothing. The agent-request marketplace passes nothing: there those
  // rows ARE the content, and omitting the parameter deliberately leaves its
  // dropdowns exactly as they are.
  // `tab` is the same idea one level down: the classic list shows three tabs
  // behind ONE modal and each tab is a different queryset ("my-tenders" = the
  // club's own rows in any status, "saved" = the caller's bookmarks,
  // "all-tenders" = the active marketplace). Pass the open tab and the counts
  // describe the rows actually on screen. `is_lite` mirrors the TP-Lite sub-tab
  // the list always sends. Omitting either keeps the endpoint's original
  // behaviour, so nothing can change by accident.
  getTenderFilters: async ({
    country = "",
    continent = "",
    tender_category = "",
    surface = "",
    tab = "",
    is_lite = undefined,
    signal,
  } = {}) => {
    try {
      const params = {};
      if (country) params.country = country;
      if (continent) params.continent = continent;
      if (tender_category) params.tender_category = tender_category;
      if (surface) params.surface = surface;
      if (tab) params.tab = tab;
      if (is_lite !== undefined && is_lite !== null) {
        params.is_lite = is_lite ? "true" : "false";
      }
      const response = await api.get("/dashboard/tenders/filters/", {
        params,
        signal,
      });
      return {
        success: true,
        data: {
          countries: response.data?.countries || [],
          divisions: response.data?.divisions || [],
          positions: response.data?.positions || [],
          continents: response.data?.continents || [],
          // Request drafts: the lifecycle segment's live counters
          // (Active / Drafts / Archive) ride on this same facet response when
          // tab=my-tenders is asked for. A backend that does not send them yet
          // yields null, and the segment then renders its pills without a
          // number instead of a wrong zero.
          lifecycle_counts: response.data?.lifecycle_counts || null,
        },
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch request filters",
      };
    }
  },

  // ── Request drafts: the three publish transitions ──────────────────────
  //
  // A draft is a normal request row carrying status 'draft' plus an optional
  // `scheduled_publish_at`. Both fields are READ-ONLY over the API: the club
  // never writes a status, it states an intent, and the backend decides what
  // that means for the lifecycle (dashboard/serializers.py — `status` is not in
  // TenderCreateUpdateSerializer.Meta.fields, `scheduled_publish_at` is in
  // read_only_fields on every read serializer). Publishing is more than a
  // status flip — all clocks restart and the agent fan-out fires — and there is
  // exactly ONE server-side function that does it, which both the manual "Post
  // now" and the autopost sweep run through.
  //
  // Hence two dedicated action paths instead of a PATCH. Each answers with the
  // full club detail representation of the row, so the caller gets the new
  // truth (status, cleared schedule) in the same response.
  //
  // "Post now" on a draft: POST, no body at all. 400 if the row is not a draft,
  // 403 without `edit_tenders`, 404 outside the owning club.
  publishTenderNow: async (tenderId) => {
    try {
      const response = await api.post(`/dashboard/tenders/${tenderId}/publish/`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: draftActionError(error, "Failed to publish request"),
      };
    }
  },

  // Set or move the automatic publish date. `scheduledPublishAt` is an ISO
  // string and must lie in the future; the row stays a draft until the sweep
  // (or a manual publish) flips it.
  scheduleTenderPublish: async (tenderId, scheduledPublishAt) => {
    try {
      const response = await api.post(
        `/dashboard/tenders/${tenderId}/schedule/`,
        { scheduled_publish_at: scheduledPublishAt },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: draftActionError(error, "Failed to schedule request"),
      };
    }
  },

  // Take the date off again. Same endpoint, and the key MUST be present and
  // explicitly null — the action answers 400 "This field is required (send null
  // to remove the date)." to a body that simply omits it, so an empty body
  // would not clear anything. The row stays a draft, it just stops being due.
  clearTenderSchedule: async (tenderId) => {
    try {
      const response = await api.post(
        `/dashboard/tenders/${tenderId}/schedule/`,
        { scheduled_publish_at: null },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: draftActionError(error, "Failed to remove the date"),
      };
    }
  },

  // ── Request archive: close, reactivate, record ─────────────────────────
  //
  // Ending a club request is a SOFT transition: POST close/ moves it into the
  // archive (the backend's terminal set), nothing is deleted, chats live on.
  // DELETE /dashboard/tenders/<id>/ remains the draft bin only — the server
  // refuses it for anything that ever was live.
  closeTender: async (tenderId) => {
    try {
      const response = await api.post(`/dashboard/tenders/${tenderId}/close/`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: draftActionError(error, "Failed to close request"),
      };
    }
  },

  // archived -> active. The server flips the row back compare-and-set style
  // (409 when it is not in the archive any more), restarts every clock and
  // notifies agents — the same consequences publishing has.
  reactivateTender: async (tenderId) => {
    try {
      const response = await api.post(
        `/dashboard/tenders/${tenderId}/reactivate/`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: draftActionError(error, "Failed to reactivate request"),
      };
    }
  },

  //
  // GET /dashboard/tenders/<id>/archive-record/ answers what happened on an
  // ended request, metadata only: summary counts plus one row per ACCEPTED
  // pitch (player, agent, agency, accepted date, outcome, and whether the
  // conversation still exists). Own club only, same permission gate as the
  // requests view — and never any chat contents.
  getArchiveRecord: async (tenderId, signal) => {
    try {
      const response = await api.get(
        `/dashboard/tenders/${tenderId}/archive-record/`,
        { signal },
      );
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error: draftActionError(error, "Failed to load the record"),
      };
    }
  },

  // Authenticated keep-alive for the in-app banner / notification button.
  keepTenderAlive: async (tenderId) => {
    try {
      const response = await api.post(
        `/dashboard/tenders/${tenderId}/keep-alive/`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to keep request active",
      };
    }
  },

  // Get the caller's own self-applications (staff/coach "Applied" tab).
  // /dashboard/staff/pitches/ (BE StaffPitchViewSet, dashboard/urls.py:139)
  // scopes to created_by=request.user.profile and accepts ?status= and
  // ?search= exactly like the agent twin below. NOTE (verified in the BE):
  // the viewset gates on the 'Staff' group — an account that only carries
  // the 'Coach' group gets an empty list. Such accounts cannot CREATE
  // pitches either (CreatePitchViewSet.perform_create requires the Staff
  // group for staff requests), so the list is consistent with what they
  // could ever have; widening both gates to Coach is a BE follow-up.
  getStaffPitches: async (params, signal) => {
    try {
      const response = await api.get("/dashboard/staff/pitches/", {
        params,
        signal,
      });

      return {
        success: true,
        data: response.data.results || [],
        pagination: {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: parseInt(params.get("page") || "1"),
        },
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        throw error;
      }
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch your applications",
      };
    }
  },

  // Get agent's pitches
  getAgentPitches: async (params, signal) => {
    try {
      const response = await api.get("/dashboard/agent/pitches/", {
        params,
        signal,
      });

      return {
        success: true,
        data: response.data.results || [],
        pagination: {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: parseInt(params.get("page") || "1"),
        },
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        throw error;
      }
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch agent pitches",
      };
    }
  },

  // Get club's own tenders
  getClubTenders: async (params, signal) => {
    try {
      const actualParams =
        params instanceof URLSearchParams
          ? params
          : new URLSearchParams(params);
      if (!actualParams.has("mine")) actualParams.append("mine", "true");

      const response = await api.get("/dashboard/tenders/", {
        params: actualParams,
        signal,
      });

      return {
        success: true,
        data: response.data.results || [],
        pagination: {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: parseInt(actualParams.get("page") || "1"),
        },
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        throw error;
      }
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch club requests",
      };
    }
  },

  // Get pitches for a tender. Optional `freeAgentsOnly` maps to the
  // backend `free_agents_only=true` query param — clubs use it to
  // filter the received-pitches list to no-fee submissions only.
  getPitches: async (
    tenderId,
    status = "active",
    signal,
    { freeAgentsOnly = false } = {},
  ) => {
    try {
      const params = { tender_id: tenderId, status };
      if (freeAgentsOnly) params.free_agents_only = "true";
      const response = await api.get(`/dashboard/club/tender-pitches/`, {
        params,
        signal,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        throw error;
      }
      return {
        success: false,
        error: error.response?.data?.message || "Failed to fetch pitches",
      };
    }
  },

  // New function to update pitch status (accept/reject/scouting/deal/signed)
  updatePitchStatus: async (pitchId, status) => {
    try {
      const response = await api.patch(
        `/dashboard/club/tender-pitches/${pitchId}/update_status/`,
        {
          // Valid statuses: 'accepted', 'rejected', 'scouting', 'deal',
          // 'signed', 'external_deal'. 'scouting' is not a decision — it
          // extends the pitch by 3 days (max 3 times, enforced backend-side in
          // dashboard/pitch_lifecycle.py).
          //
          // 'reject_and_ban' used to be listed here and never existed: no
          // caller ever sent it, and banning is not a pitch status at all —
          // it is its own endpoint (bannedAgentService.ban) and deliberately
          // leaves the pitch alone (PART A: existing pitches of a banned agent
          // continue their normal lifecycle untouched).
          status: status,
        },
      );
      // Return the full response data which might include the channel object on accept
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error(
        `Error updating pitch status to ${status}:`,
        error.response?.data || error.message,
      );
      return {
        success: false,
        error: error?.response?.data?.message || `Failed to ${status} pitch`,
      };
    }
  },

  createTender: async (formData) => {
    try {
      const response = await api.post("/dashboard/tenders/", formData);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      // T004: DRF validation errors arrive as {field: ["msg", ...]} without a
      // message/detail key, so the create used to collapse a 400 into the
      // generic fallback. Mirrors agentTenderApi.firstFieldError/fail
      // (FE PR #156, src/services/agentTenderApi.js:41-61): surface the first
      // field message AND hand the raw dict back so the wizard can render
      // inline errors at the offending field.
      const data = error?.response?.data;
      const fieldErrors =
        data && typeof data === "object" && !Array.isArray(data) ? data : null;
      let firstFieldError = null;
      if (fieldErrors) {
        for (const [field, messages] of Object.entries(fieldErrors)) {
          const msg = Array.isArray(messages) ? messages[0] : messages;
          if (typeof msg === "string" && msg) {
            firstFieldError =
              field === "non_field_errors" ? msg : `${field}: ${msg}`;
            break;
          }
        }
      }
      return {
        success: false,
        error: data?.message || firstFieldError || "Failed to create request",
        fieldErrors,
      };
    }
  },

  updateTender: async (tenderId, payload) => {
    try {
      const response = await api.patch(
        `/dashboard/tenders/${tenderId}/`,
        payload,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      // Same reader the two draft actions use. The edit path is the third leg
      // of the drafts contract — a PATCH may carry `publish_mode`, and a row
      // that is no longer a draft is refused with
      // {"publish_mode": "Only a draft can be published or scheduled."}, a body
      // with no `message` key. Additive: a backend that does send `message`
      // still wins, the generic sentence still catches a bodyless failure.
      return {
        success: false,
        error: draftActionError(error, "Failed to update request"),
      };
    }
  },

  // Get saved tenders
  getSavedTenders: async (params, signal) => {
    try {
      const response = await api.get("/dashboard/tenders/saved/", {
        params,
        signal,
      });

      return {
        success: true,
        data: response.data.results || [],
        pagination: {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: parseInt(params.get("page") || "1"),
        },
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        throw error;
      }
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch saved requests",
      };
    }
  },

  // Save tender
  saveTender: async (tenderId) => {
    try {
      const response = await api.post(
        `/dashboard/tenders/${tenderId}/save_tender/`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.error || "Failed to save request",
      };
    }
  },

  // Unsave tender
  unsaveTender: async (tenderId) => {
    try {
      const response = await api.post(
        `/dashboard/tenders/${tenderId}/unsave_tender/`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.error || "Failed to unsave request",
      };
    }
  },

  // Create pitch
  createPitch: async (data) => {
    try {
      let payload = data;
      let config = {};

      const containsFile =
        data instanceof FormData
          ? true
          : !!(
              data &&
              (data.document instanceof Blob || data.document instanceof File)
            );

      if (data instanceof FormData) {
        config.headers = { "Content-Type": "multipart/form-data" };
      } else if (containsFile) {
        const formData = new FormData();
        Object.entries(data || {}).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          formData.append(key, value);
        });
        payload = formData;
        config.headers = { "Content-Type": "multipart/form-data" };
      } else {
        // JSON body; allow axios to set default headers
      }

      const response = await api.post(
        "/dashboard/agent/pitches/create/",
        payload,
        config,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const errData = error?.response?.data;
      const toleranceError = Array.isArray(errData?.non_field_errors)
        ? errData.non_field_errors[0]
        : null;
      const mandateError = Array.isArray(errData?.mandate)
        ? errData.mandate[0]
        : null;
      const message =
        toleranceError ||
        mandateError ||
        errData?.detail ||
        errData?.message ||
        errData?.error ||
        "Failed to create pitch";
      return {
        success: false,
        error: message,
      };
    }
  },

  // Delete tender — the DRAFT bin only. A request that was ever live is
  // closed (closeTender) and archived instead; the server refuses DELETE for
  // non-drafts, so no new caller should reach for this outside the draft flow.
  deleteTender: async (tenderId) => {
    try {
      await api.delete(`/dashboard/tenders/${tenderId}/`);
      return {
        success: true,
      };
    } catch (error) {
      console.log("error", error);
      return {
        success: false,
        error: error?.response?.data?.detail || "Failed to delete request",
      };
    }
  },

  // Create staff pitch
  createStaffPitch: async (data) => {
    try {
      let payload = data;
      let config = {};

      const containsFile =
        data instanceof FormData
          ? true
          : !!(
              data &&
              (data.document instanceof Blob || data.document instanceof File)
            );

      if (data instanceof FormData) {
        config.headers = { "Content-Type": "multipart/form-data" };
      } else if (containsFile) {
        const formData = new FormData();
        Object.entries(data || {}).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          formData.append(key, value);
        });
        payload = formData;
        config.headers = { "Content-Type": "multipart/form-data" };
      }

      const response = await api.post(
        "/dashboard/agent/pitches/create/",
        payload,
        config,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      const errData = error?.response?.data;
      const message =
        errData?.detail ||
        errData?.message ||
        errData?.error ||
        "Failed to create staff pitch";
      return {
        success: false,
        error: message,
      };
    }
  },
};

const playerService = {
  // Player Highlights API
  getPlayerHighlights: async () => {
    try {
      const response = await api.get("/users/profile/player/highlights/");
      return {
        success: true,
        data: response.data.results || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch player highlights",
      };
    }
  },

  createPlayerHighlight: async (highlightData) => {
    try {
      const response = await api.post(
        "/users/profile/player/highlights/",
        highlightData,
      );
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create highlight",
      };
    }
  },

  bulkUpsertPlayerHighlights: async (highlights) => {
    try {
      const response = await api.post(
        "/users/profile/player/highlights/",
        highlights,
      );
      return {
        success: true,
        data: response.data.results || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to save player highlights",
      };
    }
  },

  updatePlayerHighlight: async (id, highlightData) => {
    try {
      const response = await api.patch(
        `/users/profile/player/highlights/${id}/`,
        highlightData,
      );
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to update highlight",
      };
    }
  },

  deletePlayerHighlight: async (id) => {
    try {
      await api.delete(`/users/profile/player/highlights/${id}/`);
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to delete highlight",
      };
    }
  },

  // Agent-managed player highlights
  getAgentPlayerHighlights: async (wyscoutId) => {
    try {
      const response = await api.get(
        `/users/agent/players/${wyscoutId}/highlights/`,
      );
      return {
        success: true,
        data: response.data.results || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch player highlights",
      };
    }
  },

  createAgentPlayerHighlight: async (wyscoutId, highlightData) => {
    try {
      const response = await api.post(
        `/users/agent/players/${wyscoutId}/highlights/`,
        highlightData,
      );
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create highlight",
      };
    }
  },

  updateAgentPlayerHighlight: async (wyscoutId, id, highlightData) => {
    try {
      const response = await api.patch(
        `/users/agent/players/${wyscoutId}/highlights/${id}/`,
        highlightData,
      );
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to update highlight",
      };
    }
  },

  deleteAgentPlayerHighlight: async (wyscoutId, id) => {
    try {
      await api.delete(`/users/agent/players/${wyscoutId}/highlights/${id}/`);
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to delete highlight",
      };
    }
  },

  // Public highlights endpoint (no auth)
  getPublicPlayerHighlights: async (sportmonksId) => {
    try {
      const response = await api.get(
        `/users/highlights/public/${sportmonksId}/`,
      );
      return {
        success: true,
        data: response.data.results || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch public highlights",
      };
    }
  },

  // Get agent's players

  getPlayers: async (params, signal) => {
    try {
      const response = await api.get("/dashboard/agent/players/", {
        params,
        signal,
      });

      if (response.data) {
        console.log("Raw player data:", response.data);
        const results = response.data.results || [];
        const normalizedResults = results.map((player) => ({
          ...player,
          // Use account_id for platform players, keep existing ID structure for compatibility
          id: player.account_id || player.id,
        }));

        return {
          success: true,
          data: normalizedResults,
          pagination: {
            count: response.data.count,
            next: response.data.next,
            previous: response.data.previous,
            currentPage: params ? parseInt(params.get("page") || "1") : 1,
          },
        };
      }

      return {
        success: false,
        error: "Invalid response format",
      };
    } catch (error) {
      console.error("Error in getPlayers:", error);
      if (error.name === "CanceledError") {
        throw error;
      }
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch players",
      };
    }
  },

  updatePlayersDetailedPosition: async (playersPayload) => {
    try {
      const response = await api.post(
        "/dashboard/agent/players/update_detailed_position/",
        {
          players: playersPayload,
        },
      );

      return {
        success: true,
        data: response.data.results || response.data,
        errors: response.data.errors || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error in updatePlayersDetailedPosition:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to update detailed positions",
      };
    }
  },

  // Update player contract expiry (Agent side)
  updateAgentPlayerContract: async (playersPayload) => {
    try {
      const response = await api.post(
        "/dashboard/agent/players/update_contract/",
        {
          players: playersPayload,
        },
      );

      return {
        success: true,
        data: response.data.results || response.data,
        errors: response.data.errors || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error in updateAgentPlayerContract:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to update contract expiry",
      };
    }
  },

  // Submit a new profile photo for a mandated player (Agent side). The photo
  // goes into review on the backend ({success: true, status: "pending"});
  // nothing changes on the profile until it is approved.
  uploadAgentPlayerPhoto: async (wyscoutPlayerId, photoFile) => {
    try {
      const formData = new FormData();
      formData.append("wyscout_player_id", wyscoutPlayerId);
      formData.append("photo", photoFile);

      const response = await api.post(
        "/dashboard/agent/players/upload_photo/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return {
        success: true,
        data: response.data.results || response.data,
        errors: response.data.errors || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error in uploadAgentPlayerPhoto:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to upload the photo. Please try again.",
      };
    }
  },

  // Paket Injuries (16.09.2026): availability + injury history as a profile fact — same contract shape as the ScoutPitch profile
  // (GET /dashboard/players/{id}/injuries/, cached a day server-side; available:false without platform data).
  getPlayerInjuries: async (playerId, signal) => {
    try {
      const response = await api.get(`/dashboard/players/${playerId}/injuries/`, { signal });
      return { success: true, data: response.data };
    } catch (error) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") throw error;
      return { success: false, error: error?.response?.data?.detail || error.message };
    }
  },

  // Get public player profile (always use ws_player_id for full stats/details)
  getPublicPlayerProfile: async (wsPlayerId) => {
    try {
      // Use new player public API endpoint
      const response = await api.get(
        `/dashboard/players/public/${wsPlayerId}/`,
      );
      if (response.data.success) {
        return {
          success: true,
          data: response.data.results,
        };
      }
      throw new Error(
        response.data.message || "Failed to fetch player profile",
      );
    } catch (error) {
      console.error("Error fetching public player profile:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch player profile",
      };
    }
  },

  // Get public coach profile (using ts_coach_id)
  getPublicCoachProfile: async (coachId) => {
    try {
      const response = await api.get(`/users/public/profile/coach/${coachId}/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching public coach profile:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch coach profile",
      };
    }
  },

  // Get extra player data

  getPlayerMatches: async (sportmonksId, params = {}) => {
    try {
      const response = await api.get(
        `/dashboard/player-extra/${sportmonksId}/matches/`,
        { params },
      );
      return {
        success: true,
        results: response.data.results,
        pagination: response.data.pagination,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch player matches",
      };
    }
  },

  getPlayerRatings: async (wyscoutId, seasonId = null) => {
    try {
      const params = seasonId ? { season_id: seasonId } : {};
      const response = await api.get(
        `/dashboard/player-extra/${wyscoutId}/ratings/`,
        { params },
      );
      const payload = response.data?.results ?? response.data;
      return { success: true, data: payload };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch player ratings",
      };
    }
  },

  getPlayerMatchHeatmap: async (matchId, playerId) => {
    try {
      const response = await api.get(`/dashboard/player-extra/match-heatmap/`, {
        params: {
          match_id: matchId,
          player_id: playerId,
        },
      });
      const payload = response.data?.results ?? response.data;
      return { success: true, data: payload };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch match heatmap",
      };
    }
  },

  getPlayerMatchHistory: async (wyscoutId, limit = 20) => {
    try {
      const response = await api.get(
        `/dashboard/player-extra/${wyscoutId}/match-history/`,
        { params: { limit } },
      );
      return {
        success: true,
        data: response.data?.results || response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch match history",
      };
    }
  },

  // Match lineup (persisted provider row, field names verbatim) — consumed by
  // components/player/MatchLineup. matchId = TheSports match id (string).
  getMatchLineup: async (matchId) => {
    try {
      const response = await api.get(
        `/dashboard/matches/${encodeURIComponent(matchId)}/lineup/`,
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        status: error?.response?.status ?? null,
        error:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to fetch match lineup",
      };
    }
  },

  getPlayerTransfers: async (wsPlayerId) => {
    try {
      const response = await api.get(
        `/dashboard/players/public/${wsPlayerId}/transfers/`,
      );
      return { success: true, data: response.data.results };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch player transfers",
      };
    }
  },

  // Market-value history for the public player profile "Value" tab.
  // Contract: { updated_at: <unix seconds|null>, history: [{ time, value,
  // currency, team_id, team_name, team_logo, age }] } — history ascending,
  // empty history = { updated_at: null, history: [] }.
  getPlayerMarketValue: async (wsPlayerId) => {
    try {
      const response = await api.get(
        `/dashboard/players/public/${wsPlayerId}/market-value/`,
      );
      // Tolerate both the raw contract shape and the results-wrapper other
      // public endpoints use.
      const payload =
        response.data && response.data.results !== undefined
          ? response.data.results
          : response.data;
      return { success: true, data: payload };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch market value history",
      };
    }
  },

  // Ability profile for the public player profile "Attributes" tab.
  // Contract: { updated_at: <unix seconds|null>, comprehensive: <int|null>,
  // ability: [[type_id, value], ...],
  // ability_detail: [[category_id, [[detail_id, value], ...]], ...] } —
  // parsing/dedupe lives in utils/playerAbility.js.
  getPlayerAbility: async (wsPlayerId) => {
    try {
      const response = await api.get(
        `/dashboard/players/public/${wsPlayerId}/ability/`,
      );
      // Tolerate both the raw contract shape and the results-wrapper other
      // public endpoints use.
      const payload =
        response.data && response.data.results !== undefined
          ? response.data.results
          : response.data;
      return { success: true, data: payload };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch player ability",
      };
    }
  },

  getPendingInvites: async () => {
    try {
      const response = await api.get(
        "/users/player-invites/list/?status=pending",
      );
      return {
        success: true,
        data: response.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch pending invites",
      };
    }
  },

  respondToInvite: async (inviteId, action) => {
    try {
      const response = await api.patch(
        `/users/mandate-invites/${inviteId}/respond/`,
        { action },
      );
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      // Handle cases where invite is already responded to gracefully
      console.log("error", error);
      if (
        error.response &&
        error.response.status === 400 &&
        error.response.data.detail.includes("already responded")
      ) {
        return {
          success: false,
          error: "This invitation has already been responded to.",
          isAlreadyResponded: true,
        };
      }
      return {
        success: false,
        error: error?.response?.data?.message || `Failed to ${action} invite`,
      };
    }
  },

  // Get available seasons for player (new API)
  getPlayerSeasons: async (wsPlayerId) => {
    try {
      const res = await api.get(
        `/dashboard/players/public/${wsPlayerId}/seasons/`,
      );
      return { success: true, data: res.data.results };
    } catch (err) {
      return {
        success: false,
        error: err?.response?.data?.message || "Failed to fetch player seasons",
      };
    }
  },

  // Get available SofaScore seasons for player (deprecated - keeping for compatibility)
  getSofaSeasons: async (sportmonksId) => {
    try {
      const res = await api.get(
        `/dashboard/player-extra/${sportmonksId}/sofascore_seasons`,
      );
      return { success: true, data: res.data.results };
    } catch (err) {
      return {
        success: false,
        error:
          err?.response?.data?.message || "Failed to fetch SofaScore seasons",
      };
    }
  },

  getHeatMap: async (playerId, uniqueTournamentId, seasonId) => {
    try {
      const res = await api.get(
        `/dashboard/players/${playerId}/heatmap/?unique_tournament_id=${uniqueTournamentId}&season_id=${seasonId}`,
      );
      return { success: true, data: res.data.results };
    } catch (err) {
      return {
        success: false,
        error: err?.response?.data?.message || "Failed to fetch heat map",
      };
    }
  },

  // Get ratings for a season
  getSeasonRatings: async (
    playerId,
    tournamentId,
    seasonId,
    type = "overall",
  ) => {
    try {
      const res = await api.get(
        `/dashboard/players/public/${playerId}/season-ratings/`,
        {
          params: {
            unique_tournament_id: tournamentId,
            season_id: seasonId,
          },
        },
      );
      return { success: true, data: res.data };
    } catch (err) {
      return {
        success: false,
        error: err?.response?.data?.message || "Failed to fetch ratings",
      };
    }
  },

  // Get player attributes
  getPlayerAttributes: async (sportmonksId) => {
    try {
      const response = await api.get(
        `/dashboard/player-extra/${sportmonksId}/attributes/`,
      );
      return { success: true, data: response.data.results || response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch player attributes",
      };
    }
  },

  // Get SportMonks stat/position types for dynamic mapping
  getSmTypes: async () => {
    try {
      const res = await api.get("/dashboard/sm-types/");
      const raw = res.data;
      // Backend might return `result`, `results`, or a bare array – normalise it.
      const payload =
        raw?.result || raw?.results || (Array.isArray(raw) ? raw : []) || [];

      return {
        success: true,
        data: payload,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch SportMonks types metadata",
      };
    }
  },

  // Get player stats (new API)
  getPlayerStats: async (
    wsPlayerId,
    wsSeasonId = null,
    sdSeasonId = null,
    sdType = "overall",
  ) => {
    try {
      const params = {};
      if (wsSeasonId) params.ws_season_id = wsSeasonId;
      if (sdSeasonId) params.sd_season_id = sdSeasonId;
      if (sdType) params.type = sdType;

      const response = await api.get(
        `/dashboard/players/public/${wsPlayerId}/stats/`,
        { params },
      );
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch player stats",
      };
    }
  },

  // Get player season statistics (deprecated - keeping for compatibility)
  getSeasonStatistics: async (
    sportmonksId,
    uniqueTournamentId,
    seasonId,
    type = "overall",
  ) => {
    try {
      const response = await api.get(
        `/dashboard/players/public/${sportmonksId}/season-statistics/`,
        {
          params: {
            unique_tournament_id: uniqueTournamentId,
            season_id: seasonId,
            type: type,
          },
        },
      );
      return { success: true, data: response.data.results || response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch season statistics",
      };
    }
  },

  // NEW: Toggle claim representation
  toggleClaimRepresentation: async (claimRepresentation) => {
    try {
      const response = await api.put("/users/profile/player/", {
        claim_representation: claimRepresentation,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to update claim representation",
      };
    }
  },

  // NEW: Send representation request to agent
  sendRepresentationRequest: async (agentAccountId) => {
    try {
      const response = await api.post(
        `/users/agents/${agentAccountId}/representation-requests/`,
        {},
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to send representation request",
      };
    }
  },

  // NEW: Dismiss representation request (for agents)
  dismissRepresentationRequest: async (inviteId) => {
    try {
      const response = await api.delete(
        `/users/agents/representation-requests/${inviteId}/`,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to dismiss request",
      };
    }
  },

  // NEW: Get clubs that have pitched to the player
  getPitchedClubs: async (status = "pitch_sent") => {
    try {
      const response = await api.get("/users/player/pitched-clubs/", {
        params: { status },
      });
      return {
        success: true,
        data: response.data.results || [],
        count: response.data.count || 0,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch pitched clubs",
      };
    }
  },

  // Get recent player ratings
  // Required parameters: sportmonksId, seasonId, teamIds (comma-separated string)
  getRecentRatings: async (sportmonksId, seasonId, teamIds) => {
    console.log("getRecentRatings API called with:", {
      sportmonksId,
      seasonId,
      teamIds,
    });
    try {
      const params = {
        season_id: seasonId,
        limit: 5, // Fixed limit as requested
        team_ids: teamIds, // Comma-separated team IDs
      };

      console.log(
        "Making API request to:",
        `/dashboard/player-extra/${sportmonksId}/recent_ratings/`,
      );
      console.log("With params:", params);

      const response = await api.get(
        `/dashboard/player-extra/${sportmonksId}/recent_ratings/`,
        { params },
      );

      console.log("Recent ratings API response received:", response.data);

      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Recent ratings API error:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch recent ratings",
      };
    }
  },
};

const clubService = {
  // Get public club profile (using ws_team_id)
  getClubPublicProfile: async (wsTeamId) => {
    try {
      const response = await api.get(`/dashboard/clubs/public/${wsTeamId}/`);
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      console.error("Error fetching public club profile:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch club profile",
      };
    }
  },

  // Get public agency profile (using agency_id)
  getAgencyPublicProfile: async (agencyId) => {
    try {
      const response = await api.get(`/dashboard/agencies/public/${agencyId}/`);
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      console.error("Error fetching public agency profile:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch agency profile",
      };
    }
  },

  getClubSquad: async (clubId = null, seasonId = null) => {
    try {
      const params = {};

      // Add wyscout_team_id parameter if clubId is provided
      if (clubId) {
        params.wyscout_team_id = clubId;
      }

      // Add seasonId if provided
      if (seasonId) {
        params.season_id = seasonId;
      }

      const response = await api.get(`/dashboard/club/my-squad/`, { params });
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error fetching club squad:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch club squad",
      };
    }
  },

  // Get my club squad (for authenticated club user)
  getMyClubSquad: async () => {
    try {
      const response = await api.get("/dashboard/club/my-squad/");
      console.log("club my players response", response.data.results.squad);
      return {
        success: true,
        data: response.data.results.squad || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error fetching my club squad:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to retrieve players",
      };
    }
  },

  // Get my club staff (for authenticated club user)
  getMyClubStaff: async () => {
    try {
      const response = await api.get("/users/club/my/staff/");

      return {
        success: true,
        data: response.data.results || response.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error fetching my club staff:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to retrieve staff",
      };
    }
  },

  // Update availability status for multiple players
  updatePlayerAvailability: async (playerUpdates) => {
    try {
      // Expected payload: Array of objects with { player_id, status, transfer_fee?, loan_fee? }
      // Note: Backend still expects player_id for SportMonks players in SMPlayer table
      const response = await api.patch(
        "/dashboard/club/player-availability/update_availability/",
        playerUpdates,
      );
      return {
        success: true,
        data: response.data.results || response.data,
        errors: response.data.errors || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error updating player availability:", error);
      return {
        success: false,
        error:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Failed to update availability",
      };
    }
  },

  updateDetailedPositions: async (playersPayload) => {
    try {
      const response = await api.post(
        "/dashboard/club/player-availability/update_detailed_position/",
        {
          players: playersPayload,
        },
      );

      return {
        success: true,
        data: response.data.results || response.data,
        errors: response.data.errors || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error updating detailed positions:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to update detailed positions",
      };
    }
  },

  // Update player contract expiry (Club side)
  updateClubPlayerContract: async (playersPayload) => {
    try {
      const response = await api.post(
        "/dashboard/club/player-availability/update_contract/",
        {
          players: playersPayload,
        },
      );

      return {
        success: true,
        data: response.data.results || response.data,
        errors: response.data.errors || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error updating club player contract:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to update contract expiry",
      };
    }
  },

  // Submit a new profile photo for a squad player (Club side). Same review
  // contract as the agent action: the backend answers
  // {success: true, status: "pending"} and the profile photo only changes
  // once the submission is approved.
  uploadClubPlayerPhoto: async (wyscoutPlayerId, photoFile) => {
    try {
      const formData = new FormData();
      formData.append("wyscout_player_id", wyscoutPlayerId);
      formData.append("photo", photoFile);

      const response = await api.post(
        "/dashboard/club/player-availability/upload_photo/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return {
        success: true,
        data: response.data.results || response.data,
        errors: response.data.errors || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error in uploadClubPlayerPhoto:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to upload the photo. Please try again.",
      };
    }
  },

  // Remove player from squad
  removePlayerFromSquad: async (playersPayload) => {
    try {
      const response = await api.patch(
        "/dashboard/club/player-availability/remove_from_squad/",
        playersPayload,
      );

      return {
        success: true,
        data: response.data.results || response.data,
        errors: response.data.errors || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error removing player from squad:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to remove player from squad",
      };
    }
  },

  // Get club staff
  getClubStaff: async (clubId) => {
    try {
      const response = await api.get(
        `/dashboard/clubs/public/${clubId}/staff/`,
      );
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error fetching club staff:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch club staff",
      };
    }
  },

  createAgentDirectChat: async (agentAccountId) => {
    try {
      const res = await api.post("/dashboard/chat/create_direct_chat/", {
        account_id: agentAccountId,
      });

      // T031: the endpoint answers a FLAT {channel_id, channel_type} body
      // (ChatViewSet.create_direct_chat -> StreamChatService.create_direct_channel).
      // This used to read res.data.results — always undefined here — so the
      // /inbox deep link received no channel id, its effect no-oped and the
      // ChannelList's mount auto-select kept the most-recent chat open instead
      // of the freshly created one. Mirrors agentService.createAgentDirectChat,
      // which already maps the flat pair; the results fallback stays defensive.
      const payload = res.data?.results || res.data || {};
      return {
        success: true,
        data: {
          channel_id: payload.channel_id,
          channel_type: payload.channel_type,
        },
      };
    } catch (error) {
      console.error("Error creating direct chat with agent:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to create direct chat with agent",
      };
    }
  },
};

const documentService = {
  getTemplates: async () => {
    try {
      const response = await api.get("/dashboard/documents/templates/");
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      console.error("Error fetching document templates:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch templates",
      };
    }
  },

  createTemplate: async (formData) => {
    try {
      const response = await api.post(
        "/dashboard/documents/templates/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return {
        success: true,
        data: response.data.results,
        message: response.data.message,
        docusealEditorUrl: response.data.docuseal_editor_url,
      };
    } catch (error) {
      console.error("Error creating template:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to create template",
      };
    }
  },
};

export const getPitchesPendingContract = (params) => {
  return api.get("/dashboard/pitches/pending-contract/", { params });
};

export const getPitchDocuments = (params) => {
  return api.get("/dashboard/pitch-documents/", { params });
};

export const getDocumentTemplates = (params) => {
  return api.get("/dashboard/document-templates/", { params });
};

export const getContractTypes = () => {
  return api.get("/dashboard/lookups/contract_types/");
};

export const getPlayerPositions = () => {
  return api.get("/dashboard/lookups/player_positions/");
};

export const createDocumentTemplate = (formData) => {
  return api.post("/dashboard/document-templates/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const getUserSignature = () => {
  return api.get("/dashboard/signatures/get_signature/");
};

export const uploadUserSignature = (formData) => {
  return api.post("/dashboard/signatures/upload/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const deleteUserSignature = () => {
  return api.delete("/dashboard/signatures/delete/");
};

export const createPitchDocument = (data) => {
  // Only set Content-Type for JSON bodies.
  const config = {};

  if (!(data instanceof FormData)) {
    console.log("creating appplication jsnon");
    config.headers = { "Content-Type": "application/json" };
  }

  // For FormData we pass no headers; Axios will add the correct multipart boundary.
  return api.post("/dashboard/pitch-documents/", data, config);
};

export const getPitchDocumentById = (id) => {
  return api.get(`/dashboard/pitch-documents/${id}/`);
};

export const completePitchDocumentFieldSetup = (id) => {
  return api.post(`/dashboard/pitch-documents/${id}/complete_field_setup/`);
};

export const generateDocuSealToken = (data) => {
  return api.post("/dashboard/docuseal/generate_token/", data);
};

export const getSignerSigningUrl = (documentId, signerId) => {
  return api.get(
    `/dashboard/pitch-documents/${documentId}/signer/${signerId}/signing-url/`,
  );
};

// Get signed document download URL
export const getPitchDocumentDownloadUrl = (documentId) => {
  return api.get(`/dashboard/pitch-documents/${documentId}/download/`);
};

const translationService = {
  // Get available languages
  getLanguages: async () => {
    try {
      const response = await api.get("/users/translate/languages/");
      return {
        success: true,
        data: Array.isArray(response.data) ? response.data : [],
      };
    } catch (error) {
      console.error("Error fetching languages:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch languages",
      };
    }
  },

  // Translate text batch
  translateTexts: async (texts, targetLanguage) => {
    try {
      const response = await api.post("/users/translate/", {
        q: texts,
        target: targetLanguage,
      });
      return {
        success: true,
        data: response.data.translations || response.data,
      };
    } catch (error) {
      console.error("Error translating texts:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to translate texts",
      };
    }
  },
};

const freeAgentsService = {
  // Get free agents list using new Wyscout API
  getFreeAgents: async (params = {}, signal) => {
    try {
      // Cache by params for 60s, dedupe in-flight
      const response = await getWithCache("/dashboard/free-agents/", {
        params,
        signal,
        cacheMaxAgeMs: 60_000,
        dedupe: true,
      });

      return {
        success: true,
        data: response.data.results || [],
        pagination: response.data.pagination || {},
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        throw error;
      }
      console.error("Error fetching free agents:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch free agents",
      };
    }
  },

  // Get positions for filtering (Wyscout role codes)
  getPositions: async (search = "") => {
    try {
      // Return standard Wyscout position codes
      const positions = [
        { id: "GK", name: "Goalkeeper", code2: "GK", code3: "GKP" },
        { id: "DF", name: "Defender", code2: "DF", code3: "DEF" },
        { id: "MD", name: "Midfielder", code2: "MD", code3: "MID" },
        { id: "FW", name: "Forward", code2: "FW", code3: "FWD" },
      ];

      const filtered = search
        ? positions.filter((pos) =>
            pos.name.toLowerCase().includes(search.toLowerCase()),
          )
        : positions;

      return {
        success: true,
        data: filtered,
      };
    } catch (error) {
      console.error("Error fetching positions:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch positions",
      };
    }
  },

  getCompetitions: async () => {
    try {
      const response = await api.get("/dashboard/transfermarkt/competitions/");

      const normalized = (response.data.results || []).map((c) => ({
        id: c.id, // unique identifier
        title: c.title, // competition name
        image: c.image, // competition logo
        leagueLevel: c.leagueLevel,
        sortIndex: c.sortIndex,
      }));

      return {
        success: true,
        data: normalized,
      };
    } catch (error) {
      console.error("Error fetching competitions:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch competitions",
      };
    }
  },

  // Get countries for filtering
  getCountries: async () => {
    try {
      const response = await api.get("/dashboard/countries");
      return {
        success: true,
        data: Array.isArray(response.data)
          ? response.data
          : response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error fetching countries:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch countries",
      };
    }
  },

};

// Chat Export Service for general chat export (non-pitch)
const chatExportService = {
  // Step 1: Start export and get task_id
  startExport: async (channelId) => {
    try {
      const response = await api.post("/dashboard/chat/export/", {
        channel_id: channelId,
      });
      return {
        success: true,
        taskId: response.data.task_id,
      };
    } catch (error) {
      console.error("Error starting chat export:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to start chat export",
      };
    }
  },

  // Step 2: Poll export status
  getExportStatus: async (channelId, taskId) => {
    try {
      const response = await api.get("/dashboard/chat/export-status/", {
        params: { channel_id: channelId, task_id: taskId },
      });
      return {
        success: true,
        status: response.data.status, // "pending", "completed", "failed"
        url: response.data.url || null,
      };
    } catch (error) {
      console.error("Error getting export status:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to get export status",
      };
    }
  },

  // Step 3: Download exported text file
  downloadExportText: async (channelId, taskId) => {
    try {
      const response = await api.get("/dashboard/chat/export-text/", {
        params: { channel_id: channelId, task_id: taskId },
        responseType: "blob", // Important: get blob response for file download
      });

      return {
        success: true,
        blob: response.data,
      };
    } catch (error) {
      console.error("Error downloading export text:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to download chat export",
      };
    }
  },
};

// New Transfermarkt service for quick search and compare operations
const transfermarktService = {
  // Quick player search
  quickSearch: async (query, page = 0) => {
    try {
      const response = await api.get("/dashboard/transfermarkt/quick-search/", {
        params: { query, page },
      });

      // Normalize players list from varying response structures
      let playersRaw = [];
      if (Array.isArray(response.data?.results)) {
        playersRaw = response.data.results;
      } else if (Array.isArray(response.data?.players)) {
        playersRaw = response.data.players;
      } else if (Array.isArray(response.data?.results?.players)) {
        playersRaw = response.data.results.players;
      }

      // Map to standardized player objects
      const players = playersRaw.map((p) => ({
        id: p.id || p.playerID || p.playerId,
        name:
          p.name ||
          p.playerName ||
          `${p.firstName || ""} ${p.lastName || ""}`.trim(),
        market_value: p.market_value || p.marketValue || null,
        position: p.position || p.mainPosition || p.playerPositions || "",
        age: p.age || p.playerAge || null,
        club: p.club || p.clubName || p.currentClub || "",
        nationality: p.nationality || p.playerNationality || "",
        playerImage: p.playerImage || p.image || p.profileImage || "",
      }));

      return {
        success: true,
        data: players,
      };
    } catch (error) {
      console.error("Error in quickSearch:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch quick search results",
      };
    }
  },

  // Get transfers list
  getTransfers: async (params = {}, signal) => {
    try {
      // Cache by filtered params for 60s, dedupe in-flight
      const response = await getWithCache("/dashboard/transfers/", {
        params,
        signal,
        cacheMaxAgeMs: 60_000,
        dedupe: true,
      });

      return {
        success: response.data?.success || true,
        data: response.data.results || [],
        message: response.data.message,
        pagination: response.data.pagination || {},
      };
    } catch (error) {
      console.error("Error fetching transfers:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to retrieve transfers list",
      };
    }
  },

  // Get rumors list
  getRumors: async (params = {}) => {
    try {
      const response = await api.get("/dashboard/transfermarkt/rumors/", {
        params,
      });

      return {
        success: true,
        data: response.data.results || [],
        message: response.data.message,
        pagination: response.data.pagination || {},
      };
    } catch (error) {
      console.error("Error fetching rumors:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to retrieve player",
      };
    }
  },

  // Get rumor details by ID
  getRumorDetails: async (rumorId) => {
    try {
      const response = await api.get(
        `/dashboard/transfermarkt/rumors/${rumorId}/`,
      );

      return {
        success: true,
        data: response.data.results || {},
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error fetching rumor details:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to retrieve player",
      };
    }
  },
};

// Mandate Invitation Service
const mandateInvitationService = {
  // Send mandate invitation to player (using SportMonks ID)
  sendMandateInvitation: async (wyscoutId, data) => {
    try {
      const response = await api.post(
        `/users/players/${wyscoutId}/mandate-invites/`,
        data,
      );
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error sending mandate invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || error?.response?.data?.detail || "Failed to send representation invitation",
        requires_photo_upload: error?.response?.data?.requires_photo_upload || false,
      };
    }
  },

  // Check if Wyscout player has a profile photo (for agents)
  checkWyscoutPlayerPhoto: async (wyscoutId) => {
    try {
      const response = await api.get(
        `/users/agents/wyscout-players/${wyscoutId}/check-photo/`
      );
      return {
        success: response.data?.success !== false,
        has_photo: response.data?.has_photo || false,
        photo_url: response.data?.photo_url || null,
        photo_source: response.data?.photo_source || null,
      };
    } catch (error) {
      return {
        success: false,
        has_photo: false,
        error: error?.response?.data?.message || error?.response?.data?.detail || "Failed to check player photo",
      };
    }
  },

  // Upload profile photo for Wyscout player (for agents)
  uploadWyscoutPlayerPhoto: async (wyscoutId, photoFile) => {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);
      
      const response = await api.post(
        `/users/agents/wyscout-players/${wyscoutId}/upload-photo/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return {
        success: response.data?.success !== false,
        photo_url: response.data?.photo_url || null,
        photo_source: response.data?.photo_source || null,
        message: response.data?.message || "Photo uploaded successfully",
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || error?.response?.data?.detail || "Failed to upload photo",
      };
    }
  },

  // Player responds to mandate invitation
  respondToMandateInvitation: async (inviteId, action) => {
    try {
      const response = await api.patch(
        `/users/mandate-invites/${inviteId}/respond/`,
        {
          action,
        },
      );
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error responding to mandate invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to respond to representation invitation",
      };
    }
  },

  // Extend mandate duration
  extendMandate: async (mandateId, endDate) => {
    try {
      const response = await api.patch(`/users/mandates/${mandateId}/extend/`, {
        end_date: endDate,
      });
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error extending mandate:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to extend representation",
      };
    }
  },

  // Get pending mandate invitations for player
  getPendingMandateInvitations: async () => {
    try {
      const response = await api.get(
        "/users/player-invites/list/?status=pending",
      );
      console.log("response mandate invitations", response);
      return {
        success: true,
        data: response.data || [],
      };
    } catch (error) {
      console.error("Error fetching pending mandate invitations:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch pending invitations",
      };
    }
  },

  sendRepresentationRequestToAgent: async (agentId) => {
    try {
      const response = await api.post(
        `/users/agents/${agentId}/representation-requests/`,
      );
      return {
        success: true,
        data: response.data || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error sending representation request to agent:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to send representation request to agent",
        message: error?.response?.data?.message,
      };
    }
  },
};

// Staff Invitation Service
const staffInvitationService = {
  // Send staff invitation
  sendStaffInvitation: async (data) => {
    try {
      const response = await api.post("/users/staff-invites/", data);
      return {
        success: true,
        data: response.data || response.data.results,
      };
    } catch (error) {
      console.error("Error sending staff invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to send staff invitation",
      };
    }
  },

  // Get pending staff invitations for the authenticated email
  getPendingStaffInvitations: async () => {
    try {
      const response = await api.get(
        "/users/staff-invites/list/?status=pending",
      );
      return {
        success: true,
        data: response.data?.results || response.data || [],
      };
    } catch (error) {
      console.error("Error fetching pending staff invitations:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch pending invitations",
      };
    }
  },

  // Staff responds to invitation
  respondToStaffInvitation: async (inviteId, action) => {
    try {
      const response = await api.patch(
        `/users/staff-invites/${inviteId}/respond/`,
        {
          action,
        },
      );
      return {
        success: true,
        data: response.data || response.data.results,
      };
    } catch (error) {
      console.error("Error responding to staff invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to respond to staff invitation",
      };
    }
  },
};

// Coach Invitation Service
const coachInvitationService = {
  // Send coach invitation
  sendCoachInvitation: async (data) => {
    try {
      const response = await api.post("/users/coach-invites/", data);
      return {
        success: true,
        data: response.data,
        message: response.data?.message || "Coach invitation sent",
        token: response.data?.token,
      };
    } catch (error) {
      console.error("Error sending coach invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to send coach invitation",
      };
    }
  },

  // Get pending coach invitations
  getPendingCoachInvitations: async () => {
    try {
      const response = await api.get(
        "/users/coach-invites/list/?status=pending",
      );
      return {
        success: true,
        data: response.data?.results || response.data || [],
      };
    } catch (error) {
      console.error("Error fetching pending coach invitations:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch pending invitations",
      };
    }
  },

  // Coach responds to invitation
  respondToCoachInvitation: async (inviteId, action) => {
    try {
      const response = await api.patch(
        `/users/coach-invites/${inviteId}/respond/`,
        {
          action,
        },
      );
      return {
        success: true,
        data: response.data || response.data.results,
      };
    } catch (error) {
      console.error("Error responding to coach invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to respond to coach invitation",
      };
    }
  },
};

// Agent Invitation Service
const agentInvitationService = {
  // Send agent invitation
  sendAgentInvitation: async (data) => {
    try {
      const response = await api.post("/users/agent-invites/", data);
      return {
        success: true,
        data: response.data || response.data.results,
      };
    } catch (error) {
      console.error("Error sending agent invitation:", error);
      // Return the raw DRF error body so the modal can map field-level errors
      // ({field:[msg]}, non_field_errors, detail) to inline states.
      return {
        success: false,
        error: error?.response?.data || { detail: "Failed to send agent invitation" },
      };
    }
  },

  // Get pending agent invitations (confirmed endpoint, my-env a89158b).
  getPendingAgentInvitations: async () => {
    try {
      const response = await api.get(
        "/users/agency/agent-invites/?status=pending",
      );
      return {
        success: true,
        data: response.data.results || [],
      };
    } catch (error) {
      console.error("Error fetching pending agent invitations:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch pending invitations",
      };
    }
  },

  // Resend a pending invitation → { success, resend_count }.
  resendAgentInvitation: async (inviteId) => {
    try {
      const response = await api.post(`/users/agent-invites/${inviteId}/resend/`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to resend invitation",
      };
    }
  },

  // Revoke (destructive) a pending invitation → { success }.
  revokeAgentInvitation: async (inviteId) => {
    try {
      const response = await api.post(`/users/agent-invites/${inviteId}/revoke/`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to revoke invitation",
      };
    }
  },

  // Agent responds to invitation
  respondToAgentInvitation: async (inviteId, action) => {
    try {
      const response = await api.patch(
        `/users/agent-invites/${inviteId}/respond/`,
        {
          action,
        },
      );
      return {
        success: true,
        data: response.data || response.data.results,
      };
    } catch (error) {
      console.error("Error responding to agent invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to respond to agent invitation",
      };
    }
  },
};

// Club Player Invitation Service (Simple Roster Invite)
const clubPlayerInvitationService = {
  // Send club player invitation
  sendClubPlayerInvitation: async (data) => {
    try {
      const response = await api.post("/users/club-player-invites/", data);
      return {
        success: true,
        data: response.data || response.data.results,
      };
    } catch (error) {
      console.error("Error sending club player invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to send club player invitation",
      };
    }
  },
};

// Sent Invitations Service
const sentInvitationsService = {
  // Get all sent invitations
  getSentInvitations: async (params = {}) => {
    try {
      const response = await api.get("/users/sent-invitations/", {
        params,
      });
      // SentInvitationsListView sets `pagination_class = None`, so the BE
      // answers with a BARE ARRAY — reading `.results` off it yielded
      // undefined and every sent invitation was dropped on the floor
      // ("No invitations found" with rows on the wire). Parse both shapes,
      // the same way the sibling services in this file do (getTenderDivisions,
      // getCountries, getLeagues), so the page keeps working if the endpoint
      // is ever paginated. The BE array response is left alone — other
      // consumers may rely on it.
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      return {
        success: true,
        data: list,
        pagination: response.data?.pagination || {},
      };
    } catch (error) {
      console.error("Error fetching sent invitations:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch sent invitations",
      };
    }
  },
};

// Notifications Service
const notificationService = {
  // Get unread notifications (polling fallback)
  getUnreadNotifications: async () => {
    try {
      const response = await api.get("/users/notifications/?seen=false");
      return {
        success: true,
        data: response.data.results || [],
        pagination: response.data.pagination || {},
      };
    } catch (error) {
      console.error("Error fetching unread notifications:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch notifications",
      };
    }
  },

  // Get all notifications with pagination
  getAllNotifications: async (params = {}) => {
    try {
      const response = await api.get("/users/notifications/", {
        params,
      });
      return {
        success: true,
        data: response.data.results || [],
        pagination: response.data.pagination || {},
      };
    } catch (error) {
      console.error("Error fetching all notifications:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch notifications",
      };
    }
  },

  // Mark specific notification as read
  markNotificationAsRead: async (notificationId) => {
    try {
      const response = await api.patch(
        `/users/notifications/${notificationId}/mark-read/`,
      );
      return {
        success: true,
        data: response.data || {},
      };
    } catch (error) {
      console.error("Error marking notification as read:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to mark notification as read",
      };
    }
  },

  // Mark all notifications as read (PATCH /users/notifications/)
  markAllNotificationsAsRead: async () => {
    try {
      const response = await api.patch("/users/notifications/");
      return {
        success: true,
        data: response.data || {},
      };
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to mark all notifications as read",
      };
    }
  },

  // Delete specific notification
  deleteNotification: async (notificationId) => {
    try {
      const response = await api.delete(
        `/users/notifications/${notificationId}/`,
      );
      return {
        success: true,
        data: response.data || {},
      };
    } catch (error) {
      console.error("Error deleting notification:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to delete notification",
      };
    }
  },

  // Clear all notifications
  clearAllNotifications: async () => {
    try {
      const response = await api.delete("/users/notifications/clear-all/");
      return {
        success: true,
        data: response.data || {},
      };
    } catch (error) {
      console.error("Error clearing all notifications:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to clear all notifications",
      };
    }
  },
};

// ============================================================
// Notification preferences (per-category in_app / email + master mute)
// GET/PUT /users/notification-preferences/
// ============================================================
const notificationPreferenceService = {
  // Read the current user's notification preferences.
  // Expected shape: { notifications_muted: bool, preferences: [
  //   { category: "tenders", in_app: true, email: true }, ... ] }
  getPreferences: async () => {
    try {
      const response = await api.get("/users/notification-preferences/");
      return {
        success: true,
        data: response.data || {},
      };
    } catch (error) {
      console.error("Error fetching notification preferences:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to load notification preferences",
      };
    }
  },

  // Persist updated preferences. `payload` mirrors the GET shape:
  // { notifications_muted, preferences: [{ category, in_app, email }] }
  updatePreferences: async (payload) => {
    try {
      const response = await api.put(
        "/users/notification-preferences/",
        payload,
      );
      return {
        success: true,
        data: response.data || {},
      };
    } catch (error) {
      console.error("Error updating notification preferences:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to save notification preferences",
      };
    }
  },
};

// ======================
// Meetings (Zoom) APIs
// ======================
const meetingService = {
  // Get all meetings with pagination
  getMeetings: async (page = 1, limit = 10) => {
    try {
      const response = await api.get("/dashboard/meetings/", {
        params: { page, limit },
      });
      return {
        success: true,
        data: response.data.results || response.data,
        pagination: {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: page,
        },
      };
    } catch (error) {
      console.error("Error fetching meetings:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch meetings",
      };
    }
  },

  // Create a new Zoom meeting
  createMeeting: async (payload) => {
    try {
      const response = await api.post("/dashboard/meetings/", payload);
      return {
        success: true,
        data: response.data,
        message: response.data.message || "Meeting created successfully",
      };
    } catch (error) {
      console.error("Error creating meeting:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to create meeting",
      };
    }
  },

  // Get meeting by ID
  getMeetingById: async (id) => {
    try {
      const response = await api.get(`/dashboard/meetings/${id}/`);
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error fetching meeting details:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch meeting details",
      };
    }
  },

  // Update existing meeting
  updateMeeting: async (id, payload) => {
    try {
      const response = await api.patch(`/dashboard/meetings/${id}/`, payload);
      return {
        success: true,
        data: response.data,
        message: response.data.message || "Meeting updated successfully",
      };
    } catch (error) {
      console.error("Error updating meeting:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to update meeting",
      };
    }
  },

  // Delete meeting
  deleteMeeting: async (id) => {
    try {
      await api.delete(`/dashboard/meetings/${id}/`);
      return {
        success: true,
        message: "Meeting deleted successfully",
      };
    } catch (error) {
      console.error("Error deleting meeting:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to delete meeting",
      };
    }
  },

  // Join meeting (get join URL)
  joinMeeting: async (meetingId) => {
    try {
      const response = await api.get(`/dashboard/meetings/${meetingId}/join/`);
      return {
        success: true,
        data: response.data,
        joinUrl: response.data.join_url,
        startUrl: response.data.start_url,
      };
    } catch (error) {
      console.error("Error joining meeting:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to join meeting",
      };
    }
  },

  // Get meeting participants
  getMeetingParticipants: async (meetingId) => {
    try {
      const response = await api.get(
        `/dashboard/meetings/${meetingId}/participants/`,
      );
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error fetching meeting participants:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch participants",
      };
    }
  },

  // Get upcoming meetings
  getUpcomingMeetings: async (page = 1, limit = 10) => {
    try {
      const response = await api.get("/dashboard/meetings/upcoming/", {
        params: { page, limit },
      });
      return {
        success: true,
        data: response.data.results || response.data,
        pagination: {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: page,
        },
      };
    } catch (error) {
      console.error("Error fetching upcoming meetings:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch upcoming meetings",
      };
    }
  },

  // Get past meetings
  getPastMeetings: async (page = 1, limit = 10) => {
    try {
      const response = await api.get("/dashboard/meetings/past/", {
        params: { page, limit },
      });
      return {
        success: true,
        data: response.data.results || response.data,
        pagination: {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
          currentPage: page,
        },
      };
    } catch (error) {
      console.error("Error fetching past meetings:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch past meetings",
      };
    }
  },

  // Send meeting reminder
  sendMeetingReminder: async (meetingId) => {
    try {
      const response = await api.post(
        `/dashboard/meetings/${meetingId}/remind/`,
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message || "Reminder sent successfully",
      };
    } catch (error) {
      console.error("Error sending meeting reminder:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to send reminder",
      };
    }
  },
};

// Legacy exports for backward compatibility
export const getMeetings = meetingService.getMeetings;
export const createMeeting = meetingService.createMeeting;
export const getMeetingById = meetingService.getMeetingById;
export const updateMeeting = meetingService.updateMeeting;
export const deleteMeeting = meetingService.deleteMeeting;

// ======================
// Contacts APIs
// ======================
const contactService = {
  // List all contacts (GET /contacts/)
  getContacts: async (params = {}) => {
    try {
      const response = await api.get("/users/contacts/", { params });
      return {
        success: true,
        data: Array.isArray(response.data)
          ? response.data
          : response.data.results || [],
        pagination: response.data.pagination || {},
      };
    } catch (error) {
      console.error("Error fetching contacts:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to fetch contacts",
      };
    }
  },

  // Create a new contact (POST /contacts/)
  createContact: async (payload) => {
    try {
      const response = await api.post("/users/contacts/", payload);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error creating contact:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to create contact",
      };
    }
  },

  // Get a specific contact (GET /contacts/{id}/)
  getContact: async (id) => {
    try {
      const response = await api.get(`/users/contacts/${id}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error fetching contact:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to fetch contact",
      };
    }
  },

  // Update existing contact (PUT /contacts/{id}/ or PATCH /contacts/{id}/)
  updateContact: async (id, payload, usePatch = true) => {
    try {
      const method = usePatch ? api.patch : api.put;
      const response = await method(`/users/contacts/${id}/`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error updating contact:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to update contact",
      };
    }
  },

  // Delete a contact (DELETE /contacts/{id}/)
  deleteContact: async (id) => {
    try {
      await api.delete(`/users/contacts/${id}/`);
      return { success: true };
    } catch (error) {
      console.error("Error deleting contact:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to delete contact",
      };
    }
  },
};

// ======================
// Business Card APIs
// ======================
const businessCardService = {
  // Get current user's card (returns {} if none)
  getMyCard: async () => {
    try {
      const response = await api.get("/users/business-card/");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error fetching business card:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to fetch business card",
      };
    }
  },

  // Create new card
  createCard: async (payload) => {
    try {
      const response = await api.post("/users/business-card/", payload);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error creating business card:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to create business card",
      };
    }
  },

  // Update existing card (PUT/PATCH)
  updateCard: async (id, payload, usePatch = true) => {
    try {
      const method = usePatch ? api.patch : api.put;
      const response = await method(`/users/business-card/${id}/`, payload);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error updating business card:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to update business card",
      };
    }
  },

  // Delete card
  deleteCard: async (id) => {
    try {
      await api.delete(`/users/business-card/${id}/`);
      return { success: true };
    } catch (error) {
      console.error("Error deleting business card:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to delete business card",
      };
    }
  },

  // Generate sharable HTML of the card
  generateShareHtml: async () => {
    try {
      const response = await api.get("/users/send-card/");
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error generating shareable business card HTML:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to generate shareable business card",
      };
    }
  },

  // Save another user's business card as contact
  saveBusinessCard: async (businessCardId) => {
    try {
      const response = await api.post("/users/save-business-card/", {
        business_card_id: businessCardId,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error saving business card as contact:", error);
      return {
        success: false,
        error: error?.response?.data || "Failed to save business card",
      };
    }
  },
};

// ======================
// Stream Token APIs
// ======================
const streamTokenService = {
  // Refresh Stream token for video/chat
  refreshStreamToken: async () => {
    try {
      const response = await api.post("/users/stream-token/refresh/", {});
      return { success: true, token: response.data.stream_chat_id };
    } catch (error) {
      console.error("Stream token refresh failed:", error);
      return {
        success: false,
        error: error.response?.data?.error || "Failed to refresh stream token",
      };
    }
  },
};

// Add Agency Admin API endpoints
export const getAgencyDashboardOverview = () =>
  api.get("/users/agency/dashboard/overview/");

export const getAgencyDashboardRecentActivity = (limit = 10) =>
  api.get(`/users/agency/dashboard/recent-activity/?limit=${limit}`);

export const getAgencyTopAgents = (limit = 3) =>
  api.get(`/users/agency/top-agents?limit=${limit}`);

export const getAgencyProfile = () => api.get("/users/agency/profile/");
// Update agency profile (name, website, document)
export const updateAgencyProfile = (data) => {
  const config =
    data instanceof FormData
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : {};
  return api.put("/users/agency/profile/", data, config);
};

export const getAgencyAgents = (params = {}, signal) =>
  api.get("/users/agency/agents", { params, signal });

export const getAgencyPlayers = (params = {}) =>
  api.get("/users/agency/players", { params });

export const getPlayersEntries = async (accountId) => {
  try {
    // Note: URL now uses account_id parameter instead of player_id
    const response = await api.get(
      `/users/agency/players/${accountId}/entries`,
    );

    console.log(response);
    return { success: true, data: response.data };
  } catch (error) {
    console.log(error);
    const newError = formatErrorMessage(error);
    return {
      success: false,
      error: newError,
    };
  }
};

// Partner APIs — RC-1 rebuild (my-env efd171f/d31e627). Consent is modelled
// agency↔agency; the wall is one flat list of linked + showcase items.

// The public/own wall: flat list of {type:linked|showcase, state, ...}.
export const getAgencyPartners = (params = {}) =>
  api.get("/users/agency/partner-wall/", { params });

// Private consent inbox: incoming pending partnership invites (receiver side).
export const getPartnerIncoming = async () => {
  try {
    const response = await api.get("/users/agency/partner-incoming/");
    return { success: true, data: response.data?.results || [] };
  } catch (error) {
    return { success: false, error: error?.response?.data || "Failed to load incoming invites" };
  }
};

// Agency search for the invite modal — self + already-linked excluded
// backend-side (the real RC-1 fix; no FE self-filter). Returns agency_id.
export const searchPartnerAgencies = async (search) => {
  try {
    const response = await api.get("/users/agency/partner-search/", { params: { search } });
    return { success: true, data: response.data?.results || [] };
  } catch (error) {
    return { success: false, error: error?.response?.data || "Agency search failed" };
  }
};

// Linked-partner invite — agency-ID based (not email).
export const sendPartnerInvitation = async (agencyId) => {
  try {
    const response = await api.post("/users/agency/partner-invite/", { agency_id: agencyId });
    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error sending partner invite:", error);
    return { success: false, error: error?.response?.data || "Failed to send partner invite" };
  }
};

// Consent transitions. accept/decline go through .../respond/ (receiver only);
// revoke is its own endpoint (either side).
export const respondToPartnerInvite = async (partnerId, action) => {
  try {
    const url =
      action === "revoke"
        ? `/users/agency/partners/${partnerId}/revoke/`
        : `/users/agency/partners/${partnerId}/respond/`;
    const body = action === "revoke" ? undefined : { action };
    const response = await api.post(url, body);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error?.response?.data || `Failed to ${action}` };
  }
};

// P-1 — true resend of a PENDING linked-partnership invite (sender-side
// admins only). No status change: the backend re-fires the recipient
// notification on the EXISTING Partner row; repeatable while pending.
// 4xx bodies come back as {detail} (400 non-pending, 403 non-sender,
// 404 unknown).
export const resendPartnerInvite = async (partnerId) => {
  try {
    const response = await api.post(`/users/agency/partners/${partnerId}/resend/`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error?.response?.data || "Failed to resend invite" };
  }
};

// Showcase (external) partner — manual add / remove, no consent flow.
export const addShowcasePartner = async ({ name, type_label, website, logo }) => {
  try {
    const fd = new FormData();
    fd.append("name", name);
    if (type_label) fd.append("type_label", type_label);
    if (website) fd.append("website", website);
    if (logo) fd.append("logo", logo);
    const response = await api.post("/users/agency/showcase-partners/", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error?.response?.data || "Failed to add partner" };
  }
};

export const removeShowcasePartner = async (id) => {
  try {
    const response = await api.delete(`/users/agency/showcase-partners/${id}/`);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error?.response?.data || "Failed to remove partner" };
  }
};

// Players board — derived contract radar + filters/sort/counts (isolated
// endpoint; Sachin's /agency/players untouched).
export const getAgencyPlayersBoard = (params = {}) =>
  api.get("/users/agency/players/board", { params });

// Change-agent picker source (now includes avatar_url + player_count).
export const getAgencyAgentsDropdown = () =>
  api.get("/users/agency/agents/dropdown");

// My Agency V2 shell (wave 2, BE PR #170) — cross-tab count pills +
// page-subtitle source (F-2/F-3): {data:{name, member_count, agents,
// players, staff, partners}}; `staff` is null when the caller cannot
// see the staff board (flag off or non-admin) → render NO staff pill.
export const getAgencyTabCounts = () => api.get("/users/agency/tab-counts");

// Agency trust stats (F-1, board 1a): {data:{requests_posted,
// verified_players, deals_closed}}. Raw promise — the shell hook wraps
// both calls fail-silent (counts/stats never block the page).
export const getAgencyTrustStats = () => api.get("/users/agency/trust-stats");

// Set an agent's curated display title (design 2g, endpoint a89158b). Without
// account_id the caller edits their OWN profile (member); with account_id an
// AgencyAdmin sets a member of their agency. Pass "" to clear.
export const setAgentDisplayTitle = async (displayTitle, accountId) => {
  try {
    const body = { display_title: displayTitle ?? "" };
    if (accountId != null) body.account_id = accountId;
    const response = await api.patch("/users/agency/agents/display-title/", body);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error?.response?.data || "Failed to update title" };
  }
};

// W1-06: set the full display-titles list (multiple titles and/or free custom
// text, same endpoint). Entries are enum values from DISPLAY_TITLE_OPTIONS or
// custom strings (max 60 chars BE-side). Pass [] to clear. Without account_id
// the caller edits their OWN profile; with account_id an AgencyAdmin sets a
// member of their agency.
export const setAgentDisplayTitles = async (displayTitles, accountId) => {
  try {
    const body = { display_titles: displayTitles || [] };
    if (accountId != null) body.account_id = accountId;
    const response = await api.patch("/users/agency/agents/display-title/", body);
    return { success: true, data: response.data };
  } catch (error) {
    return { success: false, error: error?.response?.data || "Failed to update titles" };
  }
};

// Remove an agent from the agency (destructive). 400 = last-admin guard.
export const removeAgentFromAgency = async (accountId) => {
  try {
    const response = await api.post("/users/agency/agents/remove/", { account_id: accountId });
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.message || error?.response?.data || "Failed to remove agent",
    };
  }
};

export const getAgencyPitches = (params = {}) =>
  api.get("/users/agency/pitches", { params });

export const getAgencyTasks = (params = {}) =>
  api.get("/users/agency/tasks", { params });

// Boost pitch API
export const boostPitch = async (pitchId) => {
  try {
    const response = await api.post(
      `/dashboard/agent/pitches/create/${pitchId}/boost/`,
    );
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to boost pitch",
    };
  }
};

// Boost tender API
export const boostTender = async (tenderId) => {
  try {
    const response = await api.post(`/dashboard/tenders/${tenderId}/boost/`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to boost request",
    };
  }
};

// Pin tender API
export const pinTender = async (tenderId) => {
  try {
    const response = await api.post(`/dashboard/tenders/${tenderId}/pin/`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to pin request",
    };
  }
};

// Unpin tender API
export const unpinTender = async (tenderId) => {
  try {
    const response = await api.post(`/dashboard/tenders/${tenderId}/unpin/`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to unpin request",
    };
  }
};

// ======================
// Admin Transfer APIs
// ======================
export const transferClubAdmin = async (accountId) => {
  try {
    const response = await api.post("/users/staff-roles/transfer-club-admin/", {
      account_id: accountId,
    });
    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.message || "Club admin transferred successfully",
      logoutRequired: !!response.data?.logout_required,
      logoutAccounts: response.data?.logout_accounts || [],
    };
  } catch (error) {
    return {
      success: false,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to transfer club admin",
    };
  }
};

export const transferAgencyAdmin = async (accountId) => {
  try {
    const response = await api.post("/users/agency/transfer-admin/", {
      account_id: accountId,
    });
    return {
      success: true,
      data: response.data?.data || response.data,
      message:
        response.data?.message || "Agency admin transferred successfully",
      // Backend stopped sending logout_required after the audit; keep
      // the field-read for backward-compat with older deployments but
      // expect undefined (falsy) on current builds.
      logoutRequired: !!response.data?.logout_required,
      logoutAccounts: response.data?.logout_accounts || [],
    };
  } catch (error) {
    return {
      success: false,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to transfer agency admin",
    };
  }
};

// Multi-admin helpers — additive promote/demote on agency + club.
// Unlike transferAgencyAdmin (which moves the single-admin slot), these
// add or remove a user from the AgencyAdmin / ClubAdmin group while
// leaving everyone else's status alone. Caller should re-fetch the
// user profile after a successful response so the Redux store picks up
// the new permissions / role on the next render.
export const agencyPromoteAdmin = async (accountId) => {
  try {
    const response = await api.post("/users/agency/promote-admin/", {
      account_id: accountId,
    });
    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.message || "User promoted to agency admin",
    };
  } catch (error) {
    return {
      success: false,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to promote user",
    };
  }
};

export const agencyDemoteAdmin = async (accountId) => {
  try {
    const response = await api.post("/users/agency/demote-admin/", {
      account_id: accountId,
    });
    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.message || "User demoted from agency admin",
    };
  } catch (error) {
    return {
      success: false,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to demote user",
    };
  }
};

export const clubPromoteAdmin = async (accountId) => {
  try {
    const response = await api.post("/users/club/promote-admin/", {
      account_id: accountId,
    });
    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.message || "User promoted to club admin",
    };
  } catch (error) {
    return {
      success: false,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to promote user",
    };
  }
};

export const clubDemoteAdmin = async (accountId) => {
  try {
    const response = await api.post("/users/club/demote-admin/", {
      account_id: accountId,
    });
    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.message || "User demoted from club admin",
    };
  } catch (error) {
    return {
      success: false,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to demote user",
    };
  }
};

// Reassign a player from one agent of the agency to another. Returns
// a summary describing which historical artefacts stayed put (deal-
// tracker entries, past pitches, chat channels) and which carried over
// to the new agent (active mandate + countries + exclusivity).
export const reassignPlayerAgent = async (playerId, newAgentAccountId) => {
  try {
    const response = await api.post(
      `/users/agency/players/${encodeURIComponent(playerId)}/reassign-agent/`,
      { new_agent_account_id: newAgentAccountId },
    );
    return {
      success: true,
      data: response.data?.data || response.data,
      message: response.data?.message || "Player reassigned",
    };
  } catch (error) {
    return {
      success: false,
      error:
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Failed to reassign player",
    };
  }
};

// Add manual entry for agency (legacy — folders endpoint preferred)
export const addManualEntry = async (payload) => {
  return api.post("/users/agency/entries/manual", payload);
};

// ----- Player Folders (Taskboard restructure) ---------------------
// Folders are the new container per (agency, player). Each folder
// holds deal-tracker entries and external links. The Taskboard grid
// reads getAgencyFolders; clicking a card navigates to the detail
// page which uses the per-folder entry / link endpoints.

export const getAgencyFolders = async (params = {}) => {
  try {
    const response = await api.get("/users/agency/folders/", { params });
    return { success: true, data: response.data?.data || [] };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to load folders",
    };
  }
};

export const createAgencyFolder = async (playerId) => {
  try {
    const response = await api.post("/users/agency/folders/", {
      player_id: playerId,
    });
    return {
      success: true,
      data: response.data?.data || response.data,
      // Backend returns 200 if folder already existed, 201 if just
      // created. The frontend can use this to decide whether to
      // show a "Folder already exists, opening..." toast or not.
      created: response.data?.created !== false,
      // `restored` is true when the folder had been soft-deleted and this
      // call brought it (and its entries) back. Distinct from `created`:
      // the user is about to see the OLD deal history, so the toast says
      // "restored", not "created".
      restored: response.data?.restored === true,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        "Failed to create folder",
    };
  }
};

export const getAgencyFolderDetail = async (folderId) => {
  try {
    const response = await api.get(
      `/users/agency/folders/${encodeURIComponent(folderId)}/`,
    );
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      status: error?.response?.status,
      error: error?.response?.data?.detail || "Failed to load folder",
    };
  }
};

export const deleteAgencyFolder = async (folderId) => {
  try {
    await api.delete(
      `/users/agency/folders/${encodeURIComponent(folderId)}/`,
    );
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to delete folder",
    };
  }
};

export const getAgencyFolderEntries = async (folderId) => {
  try {
    const response = await api.get(
      `/users/agency/folders/${encodeURIComponent(folderId)}/entries/`,
    );
    return { success: true, data: response.data?.data || [] };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to load entries",
    };
  }
};

export const createAgencyFolderEntry = async (folderId, payload) => {
  try {
    const response = await api.post(
      `/users/agency/folders/${encodeURIComponent(folderId)}/entries/`,
      payload,
    );
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to create entry",
    };
  }
};

export const getAgencyFolderLinks = async (folderId) => {
  try {
    const response = await api.get(
      `/users/agency/folders/${encodeURIComponent(folderId)}/links/`,
    );
    return { success: true, data: response.data?.data || [] };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to load links",
    };
  }
};

export const createAgencyFolderLink = async (folderId, payload) => {
  try {
    const response = await api.post(
      `/users/agency/folders/${encodeURIComponent(folderId)}/links/`,
      payload,
    );
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to add link",
    };
  }
};

export const updateAgencyFolderLink = async (folderId, linkId, payload) => {
  try {
    const response = await api.patch(
      `/users/agency/folders/${encodeURIComponent(folderId)}/links/${encodeURIComponent(linkId)}/`,
      payload,
    );
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to update link",
    };
  }
};

export const deleteAgencyFolderLink = async (folderId, linkId) => {
  try {
    await api.delete(
      `/users/agency/folders/${encodeURIComponent(folderId)}/links/${encodeURIComponent(linkId)}/`,
    );
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to delete link",
    };
  }
};

// Delete a single deal-tracker entry from a folder. Backend permission:
// AgencyAdmin can delete any entry; a regular agent can only delete entries
// they themselves created (the entry's `agent` matches the requester).
// House rule (redesign): the backend turns this into a soft-delete /
// status transition rather than a hard row delete.
export const deleteAgencyFolderEntry = async (folderId, entryId) => {
  try {
    await api.delete(
      `/users/agency/folders/${encodeURIComponent(folderId)}/entries/${encodeURIComponent(entryId)}/`,
    );
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to delete entry",
    };
  }
};

// ----- Taskboard redesign: entry edit / status / update-thread --------
// These target the additive endpoints from the SCHEMA-PLAN (entry PATCH
// for status + full edit; an append-only updates subresource with pin).
// Built UI-first: until the backend lands them they fail gracefully and
// the isolated mock layer (services/taskboardMock.js, default OFF) can
// stand in for local development.

// PATCH an entry — used both for the one-gesture status change (drag /
// quick-select: { status }) and for a full edit ({ mode_of_communication,
// date, notes, status, wyscout_team_id, club_name, club_logo, club }).
export const patchAgencyFolderEntry = async (folderId, entryId, payload) => {
  try {
    const response = await api.patch(
      `/users/agency/folders/${encodeURIComponent(folderId)}/entries/${encodeURIComponent(entryId)}/`,
      payload,
    );
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      status: error?.response?.status,
      error: error?.response?.data?.detail || "Failed to update entry",
    };
  }
};

// Append-only update on an entry. NEVER mutates the original note.
export const createAgencyFolderEntryUpdate = async (folderId, entryId, payload) => {
  try {
    const response = await api.post(
      `/users/agency/folders/${encodeURIComponent(folderId)}/entries/${encodeURIComponent(entryId)}/updates/`,
      payload,
    );
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      status: error?.response?.status,
      error: error?.response?.data?.detail || "Failed to add update",
    };
  }
};

// Pin / unpin a single update (only one pinned at a time — the backend
// enforces the invariant; the UI reflects the returned state).
export const pinAgencyFolderEntryUpdate = async (folderId, entryId, updateId, pinned) => {
  try {
    const response = await api.patch(
      `/users/agency/folders/${encodeURIComponent(folderId)}/entries/${encodeURIComponent(entryId)}/updates/${encodeURIComponent(updateId)}/pin/`,
      { pinned },
    );
    return { success: true, data: response.data?.data };
  } catch (error) {
    return {
      success: false,
      status: error?.response?.status,
      error: error?.response?.data?.detail || "Failed to pin update",
    };
  }
};

// Real club search for the entry club picker. Backed by the TheSports
// picker search (TSTeam: TS logos, world-rank ordering) — id / name /
// country / image_url. Non-platform clubs are included by design.
export const searchPickerClubs = async (q, { signal } = {}) => {
  try {
    const response = await api.get("/api/clubs/picker-search/", {
      params: { q },
      signal,
    });
    const raw = response.data?.data || response.data?.results || response.data || [];
    return { success: true, data: Array.isArray(raw) ? raw : [] };
  } catch (error) {
    if (error?.code === "ERR_CANCELED") return { success: false, canceled: true, data: [] };
    return {
      success: false,
      error: error?.response?.data?.detail || "Failed to search clubs",
      data: [],
    };
  }
};

// ======================
// Stream Export APIs (server-side required)
// ======================
const streamExportService = {
  exportChannelHistory: async (cid) => {
    try {
      const response = await api.post("/stream/export/channel/", { cid });
      return {
        success: true,
        taskId: response.data.task_id || response.data.taskId,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to start export task",
      };
    }
  },
  getExportStatus: async (taskId) => {
    try {
      const response = await api.get("/stream/export/status/", {
        params: { task_id: taskId },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to fetch export status",
      };
    }
  },
};

// ======================
// Chat Action APIs
// ======================
const normalizePitchIdForPath = (rawPitchId) => {
  if (rawPitchId === null || rawPitchId === undefined) {
    return null;
  }

  let normalized = String(rawPitchId).trim();

  if (!normalized) {
    return null;
  }

  // Handle Stream channel cid format e.g. "messaging:pitch-117"
  if (normalized.includes(":")) {
    normalized = normalized.split(":").pop();
  }

  // Handle formats like "pitch-117"
  if (normalized.startsWith("pitch-")) {
    normalized = normalized.substring("pitch-".length);
  }

  return normalized;
};

const chatActionService = {
  // Perform channel action (close, delete, archive, open)
  performChannelAction: async (channelId, action) => {
    try {
      const response = await api.post("/dashboard/chat/channel-action/", {
        channel_id: channelId,
        action: action, // "close", "delete", "archive", "open"
      });
      return {
        success: true,
        data: response.data,
        channel_id: response.data.channel_id,
        action: response.data.action,
      };
    } catch (error) {
      console.error(`Error performing ${action} action:`, error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          `Failed to ${action} chat`,
        status: error?.response?.status,
      };
    }
  },

  // D24 manual archive: move a chat into the CALLER's own archive list (per
  // member, any role). The chat stays WRITABLE — a new incoming message
  // auto-returns it. Idempotent. Backend: POST /dashboard/chat/archive/.
  archiveChat: async (channelId) => {
    try {
      const response = await api.post("/dashboard/chat/archive/", {
        channel_id: channelId,
      });
      return {
        success: true,
        data: response.data,
        channel_id: response.data?.channel_id,
      };
    } catch (error) {
      console.error("Error archiving chat:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to archive chat",
        status: error?.response?.status,
      };
    }
  },

  // D24 manual unarchive: return a chat from the CALLER's archive to the main
  // list. An EXPIRED (timer) chat is read-only and CANNOT be manually
  // unarchived — the backend answers 409 with timer_status:"expired"; the caller
  // surfaces that pointer. Backend: POST /dashboard/chat/unarchive/.
  unarchiveChat: async (channelId) => {
    try {
      const response = await api.post("/dashboard/chat/unarchive/", {
        channel_id: channelId,
      });
      return {
        success: true,
        data: response.data,
        channel_id: response.data?.channel_id,
      };
    } catch (error) {
      const status = error?.response?.status;
      console.error("Error unarchiving chat:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to unarchive chat",
        status,
        // 409 = the chat expired (read-only, revive-only); surfaced verbatim.
        expired:
          status === 409 ||
          error?.response?.data?.timer_status === "expired",
      };
    }
  },

  // Fetch available club staff for a pitch channel
  getPitchStaff: async (pitchId) => {
    try {
      const normalizedPitchId = normalizePitchIdForPath(pitchId);

      if (!normalizedPitchId) {
        throw new Error("Invalid pitch ID");
      }

      const response = await api.get(
        `/dashboard/chat/pitch/${normalizedPitchId}/staff/`,
      );

      return {
        success: true,
        data: response.data,
        results: response.data?.results || [],
      };
    } catch (error) {
      console.error("Error fetching pitch staff:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to load staff members",
        status: error?.response?.status,
      };
    }
  },

  // Add members to a pitch channel
  addPitchMembers: async (pitchId, payload) => {
    try {
      const normalizedPitchId = normalizePitchIdForPath(pitchId);

      if (!normalizedPitchId) {
        throw new Error("Invalid pitch ID");
      }

      const response = await api.post(
        `/dashboard/chat/pitch/${normalizedPitchId}/members/add/`,
        payload,
      );

      return {
        success: true,
        data: response.data,
        added_members: response.data?.added_members || [],
        skipped: response.data?.skipped || [],
        channel_id: response.data?.channel_id,
      };
    } catch (error) {
      console.error("Error adding pitch members:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to add members",
        status: error?.response?.status,
      };
    }
  },

  // Leave a pitch channel (with optional admin transfer)
  leavePitchChannel: async (pitchId, payload) => {
    try {
      const normalizedPitchId = normalizePitchIdForPath(pitchId);

      if (!normalizedPitchId) {
        throw new Error("Invalid pitch ID");
      }

      const response = await api.post(
        `/dashboard/chat/pitch/${normalizedPitchId}/members/leave/`,
        payload,
      );

      return {
        success: true,
        data: response.data,
        channel_id: response.data?.channel_id,
        removed_account_id: response.data?.removed_account_id,
        new_admin_account_id: response.data?.new_admin_account_id,
      };
    } catch (error) {
      console.error("Error leaving pitch channel:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to leave chat",
        status: error?.response?.status,
      };
    }
  },

  // Reassign pitch channel admin without leaving
  reassignPitchAdmin: async (pitchId, payload) => {
    try {
      const normalizedPitchId = normalizePitchIdForPath(pitchId);

      if (!normalizedPitchId) {
        throw new Error("Invalid pitch ID");
      }

      const response = await api.post(
        `/dashboard/chat/pitch/${normalizedPitchId}/members/reassign-admin/`,
        payload,
      );

      return {
        success: true,
        data: response.data,
        channel_id: response.data?.channel_id,
        new_admin_account_id: response.data?.new_admin_account_id,
        previous_admin_account_id: response.data?.previous_admin_account_id,
      };
    } catch (error) {
      console.error("Error reassigning pitch admin:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to change chat admin",
        status: error?.response?.status,
      };
    }
  },

  // ---------------------------------------------------------------------------
  // cid-based handover (D-008): the same four operations for ANY club<->agent
  // channel (pitch- OR direct_*), keyed by the Stream cid instead of a pitch id.
  // `cid` is the stripped id (no "messaging:" prefix), matching the timer APIs.
  // Response shapes mirror the pitch endpoints above. A 404 means the backend
  // route isn't live yet (P0 ships in parallel) -> callers hide the controls.
  // ---------------------------------------------------------------------------

  // Fetch club staff for a cid-addressed channel (add/reassign/leave pickers)
  getChannelStaff: async (cid) => {
    try {
      if (!cid) {
        throw new Error("Invalid channel id");
      }

      const response = await api.get("/dashboard/chat/channel/staff/", {
        params: { cid },
      });

      return {
        success: true,
        data: response.data,
        results: response.data?.results || [],
      };
    } catch (error) {
      console.error("Error fetching channel staff:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to load staff members",
        status: error?.response?.status,
      };
    }
  },

  // Add club staff to a cid-addressed channel
  addChannelMembers: async (cid, accountIds) => {
    try {
      if (!cid) {
        throw new Error("Invalid channel id");
      }

      const response = await api.post("/dashboard/chat/channel/members/add/", {
        cid,
        account_ids: accountIds,
      });

      return {
        success: true,
        data: response.data,
        added_members: response.data?.added_members || [],
        skipped: response.data?.skipped || [],
        channel_id: response.data?.channel_id,
      };
    } catch (error) {
      console.error("Error adding channel members:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to add members",
        status: error?.response?.status,
      };
    }
  },

  // Reassign the admin (timer controller) of a cid-addressed channel
  reassignChannelAdmin: async (cid, newAdminAccountId) => {
    try {
      if (!cid) {
        throw new Error("Invalid channel id");
      }

      const response = await api.post(
        "/dashboard/chat/channel/members/reassign-admin/",
        {
          cid,
          new_admin_account_id: newAdminAccountId,
        },
      );

      return {
        success: true,
        data: response.data,
        channel_id: response.data?.channel_id,
        new_admin_account_id: response.data?.new_admin_account_id,
        previous_admin_account_id: response.data?.previous_admin_account_id,
      };
    } catch (error) {
      console.error("Error reassigning channel admin:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to change chat admin",
        status: error?.response?.status,
      };
    }
  },

  // Leave a cid-addressed channel (admin must pass a successor)
  leaveChannel: async (cid, successorAccountId) => {
    try {
      if (!cid) {
        throw new Error("Invalid channel id");
      }

      const payload = { cid };
      if (successorAccountId !== undefined && successorAccountId !== null) {
        payload.successor_account_id = successorAccountId;
      }

      const response = await api.post(
        "/dashboard/chat/channel/members/leave/",
        payload,
      );

      return {
        success: true,
        data: response.data,
        channel_id: response.data?.channel_id,
        removed_account_id: response.data?.removed_account_id,
        new_admin_account_id: response.data?.new_admin_account_id,
      };
    } catch (error) {
      console.error("Error leaving channel:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to leave chat",
        status: error?.response?.status,
      };
    }
  },
};

// ======================
// Chat lifecycle timer APIs (Club<->Agent chats)
// Per timer-builder contract: all under /dashboard/chat/, keyed by `channel_cid`
// (the GetStream id WITHOUT the "messaging:" prefix). The backend enforces the
// club-only gate + the GetStream frozen write-lock and returns the authoritative
// status object (can_extend / can_revive / is_club / remaining_seconds / ...).
// ======================
const buildTimerError = (error) => {
  const data = error?.response?.data || {};
  return {
    success: false,
    detail: data.detail || null, // stable identifiers: invalid_duration / not_expired
    error: data.detail || data.message || data.error || "Chat timer request failed",
    status: error?.response?.status,
    notFound: error?.response?.status === 404,
  };
};

const chatTimerService = {
  // Authoritative timer state. 404 => the channel has no timer (untimed chat).
  getStatus: async (channelCid) => {
    try {
      const response = await api.get("/dashboard/chat/timer-status/", {
        params: { channel_cid: channelCid },
      });
      return { success: true, data: response.data };
    } catch (error) {
      return buildTimerError(error);
    }
  },

  // Club extends an active timer by a chosen number of days (from
  // extension_choices). Returns the refreshed status object.
  extend: async (channelCid, days) => {
    try {
      const response = await api.post("/dashboard/chat/extend-timer/", {
        channel_cid: channelCid,
        days,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error extending chat timer:", error);
      return buildTimerError(error);
    }
  },

  // Club revives an expired conversation in the same thread; a fresh window
  // starts. `days` is optional (backend falls back to the initial window).
  revive: async (channelCid, days) => {
    try {
      const payload = { channel_cid: channelCid };
      if (days !== undefined && days !== null) {
        payload.days = days;
      }
      const response = await api.post("/dashboard/chat/revive-timer/", payload);
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error reviving chat conversation:", error);
      return buildTimerError(error);
    }
  },

  // Controller turns a DEACTIVATED (∞ Permanent) thread's countdown back ON
  // (T034 — BE PR #176). Mirrors `revive` exactly: optional `days`, the backend
  // falls back to the context default (club_agent 5d / agent_agent 14d), and the
  // refreshed serialize_status object comes back so the chip flips from ∞ to a
  // countdown. 400 `not_deactivated` on an active (extend) or expired (revive)
  // timer; 403 for anyone but the rights holder.
  reactivate: async (channelCid, days) => {
    try {
      const payload = { channel_cid: channelCid };
      if (days !== undefined && days !== null) {
        payload.days = days;
      }
      const response = await api.post(
        "/dashboard/chat/reactivate-timer/",
        payload
      );
      return { success: true, data: response.data };
    } catch (error) {
      console.error("Error reactivating chat timer:", error);
      return buildTimerError(error);
    }
  },
};

// ======================
// Chat Call Outcome API (3e call-event cards)
// ======================
// Stamps the terminal outcome onto a `call_invitation` message so the 3e
// renderer can show state + direction + duration instead of a datales neutral
// card. The backend is idempotent + no-downgrade (a final completed/declined/
// outgoing is never overwritten by a later missed/no_answer), so the caller and
// the decliner can both fire without ordering concerns. `channel_cid` is the
// GetStream id WITHOUT the "messaging:" prefix — same convention the timer
// endpoints use. Best-effort: every call site swallows failures so a stamp
// error can never break the call flow.
const chatCallOutcomeService = {
  stampOutcome: async ({ message_id, channel_cid, outcome, duration_seconds }) => {
    try {
      const payload = { message_id, channel_cid, outcome };
      if (duration_seconds != null) {
        payload.duration_seconds = duration_seconds;
      }
      const response = await api.post("/dashboard/chat/call-outcome/", payload);
      return {
        success: true,
        data: response.data,
        changed: response.data?.changed,
      };
    } catch (error) {
      console.error("Error stamping call outcome:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to stamp call outcome",
        status: error?.response?.status,
      };
    }
  },
};

// ======================
// Transfer Window APIs
// ======================

// Utility function to convert new API response format to expected format
const convertTransferWindowResponse = (responseData) => {
  if (!responseData) return null;

  // If it's already in the old format, return as is
  if (responseData.window_1_start && responseData.window_1_end) {
    return responseData;
  }

  // Handle new format
  const { status, current_start, current_end, next_window, window } =
    responseData;

  if (!window) return null;

  // Convert to expected format
  return {
    ...window,
    // Add computed fields for backward compatibility
    current_start,
    current_end,
    next_window,
    status,
    // For components that expect window_1_start/end format
    window_1_start: window.window_1_start,
    window_1_end: window.window_1_end,
    window_2_start: window.window_2_start,
    window_2_end: window.window_2_end,
  };
};

const transferWindowService = {
  // Home Quick-View uses this — the dedicated TransferWindow `countries`
  // action returns ONLY countries with actual window data (~191 entries),
  // unpaginated, with proper flag-icon URLs. The legacy getCountries()
  // below still hits /dashboard/countries/ for the full /transfer-windows
  // page so we don't break that surface.
  getTransferWindowCountries: async (category = "mens_professional", { signal } = {}) => {
    try {
      const params = {};
      if (category) params.category = category;
      const response = await api.get("/dashboard/transfer-windows/countries/", {
        params, signal,
      });
      const list = Array.isArray(response.data) ? response.data : [];
      return { success: true, data: list };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch transfer-window countries",
      };
    }
  },

  getCountries: async (category = "mens_professional") => {
    try {
      const params = {};
      if (category) params.category = category;
      const response = await api.get("/dashboard/countries/", {
        params,
      });
      return {
        success: true,
        data: response.data?.results,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch transfer window countries",
      };
    }
  },
  getAllTransferWindows: async (
    country = null,
    category = "mens_professional",
    page = 1,
    pageSize = 25,
    windowLabel = null,
    { includePast = false, signal } = {},
  ) => {
    try {
      const params = {};
      if (country) params.country = country;
      if (category) params.category = category;
      if (page) params.page = page;
      if (pageSize) params.page_size = pageSize;
      if (windowLabel) params.window = windowLabel;
      // Opt-in: when true the backend skips its "exclude past" filter so
      // we can compute status (incl. "next-window-expected" fallback)
      // for countries whose current-season windows have all closed.
      if (includePast) params.include_past = "true";

      const response = await api.get("/dashboard/transfer-windows/", {
        params, signal,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      if (error?.name === "CanceledError") throw error;
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch transfer windows",
      };
    }
  },
  getCurrentWindow: async (country, category = "mens_professional") => {
    try {
      if (!country) {
        return {
          success: false,
          error: "Country parameter is required",
        };
      }

      const params = { country };
      if (category) params.category = category;

      const response = await api.get(
        "/dashboard/transfer-windows/current-window/",
        {
          params,
        },
      );

      // Convert response to expected format
      const convertedData = convertTransferWindowResponse(response.data);

      return {
        success: true,
        data: convertedData,
      };
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 400) {
        return {
          success: false,
          error: "Country parameter is required",
        };
      }
      if (error.response?.status === 404) {
        return {
          success: false,
          error: "No active or upcoming transfer window found",
        };
      }
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch current window",
      };
    }
  },
  getTopLeagues: async () => {
    try {
      const response = await api.get(
        "/dashboard/transfer-windows/top_leagues/",
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch top leagues",
      };
    }
  },
};

// ======================
// Transfer Points APIs
// ======================
const transferPointsService = {
  // Get current transfer points balance
  getBalance: async () => {
    try {
      const response = await api.get("/users/transfer-points/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch transfer points balance",
      };
    }
  },

  // Get transfer points transaction history
  getHistory: async () => {
    try {
      const response = await api.get("/users/transfer-points-history/");
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch transfer points history",
      };
    }
  },
};

// ======================
// Agent APIs
// ======================

const agentService = {
  getRecentActivity: async () => {
    try {
      const response = await api.get("/dashboard/agent/recent-activity/");
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch recent activity",
      };
    }
  },

  // Get public agent profile (using account_id)
  getAgentPublicProfile: async (accountId) => {
    try {
      const response = await api.get(`/dashboard/agents/public/${accountId}/`);
      return {
        success: true,
        data: response.data.results,
      };
    } catch (error) {
      console.error("Error fetching public agent profile:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to fetch agent profile",
      };
    }
  },

  // Create agent-to-agent direct chat
  createAgentDirectChat: async (agentAccountId) => {
    try {
      const response = await api.post("/dashboard/chat/create_agent_chat/", {
        account_id: agentAccountId,
      });
      return {
        success: true,
        data: {
          channel_id: response.data.channel_id,
          channel_type: response.data.channel_type,
        },
      };
    } catch (error) {
      console.error("Error creating agent direct chat:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to create agent chat",
      };
    }
  },

  // NEW: Get representation requests (for agents)
  getRepresentationRequests: async () => {
    try {
      const response = await api.get("/users/agents/representation-requests/");
      return {
        success: true,
        data: response.data || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch representation requests",
      };
    }
  },

  // NEW: Create agent join request
  createJoinRequest: async (agencyId) => {
    try {
      const response = await api.post("/users/agent-join-requests/", {
        agency_id: agencyId,
      });
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.detail || "Failed to create join request",
      };
    }
  },

  // NEW: Get pending agent requests (for agency admin)
  getPendingAgentRequests: async () => {
    try {
      const response = await api.get("/users/agency/agent-requests/");
      return {
        success: true,
        data: response.data.results || [],
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch pending agent requests",
      };
    }
  },

  // NEW: Respond to agent join request (for agency admin)
  respondToAgentRequest: async (inviteId, action) => {
    try {
      const response = await api.patch(
        `/users/agency/agent-requests/${inviteId}/respond/`,
        { action },
      );
      return {
        success: true,
        data: response.data,
        action: action,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.message || `Failed to ${action} agent request`,
      };
    }
  },

  // NEW: Onboarding agency selection
  selectAgencyOnboarding: async (agencyId) => {
    try {
      const response = await api.post("/users/onboarding-agency-details/", {
        agency_id: agencyId,
      });
      return {
        success: true,
        data: response.data,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to select agency",
      };
    }
  },

  // NEW: Agent leaves current agency
  leaveAgency: async () => {
    try {
      const response = await api.post("/users/agents/leave-agency/", {});
      return {
        success: true,
        data: response.data,
        message: response.data?.message || "Left agency successfully",
      };
    } catch (error) {
      return {
        success: false,
        error:
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to leave agency",
      };
    }
  },
};

// ======================
// Contact Support APIs
// ======================
const contactSupportService = {
  // Create support request
  createSupportRequest: async (data) => {
    try {
      // Check if data contains a file attachment
      let config = {};
      let payload = data;

      // Use FormData for multipart/form-data when attachment is present
      const formData = new FormData();
      formData.append("category", data.category);
      formData.append("description", data.description);
      formData.append("priority", data.priority);
      formData.append("contact_email", data.contact_email);
      formData.append("prefer_whatsapp", data.prefer_whatsapp);

      if (data.prefer_whatsapp && data.phone_number) {
        formData.append("phone_number", data.phone_number);
      }

      if (data.attachment) {
        formData.append("attachment", data.attachment);
      }

      payload = formData;
      config = {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };

      const response = await api.post(
        "/users/support/contact/",
        payload,
        config,
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error creating support request:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to create support request",
      };
    }
  },
};

// ======================
// Roles & Permissions APIs
// ======================
const rolesPermissionsService = {
  // Get all club roles
  getRoles: async () => {
    try {
      const response = await api.get("/users/roles/");
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error fetching roles:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch roles",
      };
    }
  },

  // Get available permissions
  getAvailablePermissions: async () => {
    try {
      const response = await api.get("/users/roles/available_permissions/");
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error fetching permissions:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch permissions",
      };
    }
  },

  // Create a club-owned role (POST /users/roles/). The backend pins `club`
  // itself via get_user_club(request.user) — see users/role_views.py
  // ClubRoleViewSet.perform_create — so the caller only ever sends `name`.
  // The new row comes back is_editable:true (model default) and unique per
  // (name, club); a duplicate name in this club answers 400.
  createRole: async (name) => {
    try {
      const response = await api.post("/users/roles/", { name });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error creating role:", error);
      const data = error?.response?.data;
      // DRF field errors arrive as arrays keyed by field name
      // (e.g. {"name": ["..."]}) or as {"non_field_errors": [...]}. Never
      // surface that raw text to the user — the caller maps status/shape to
      // its own copy — but keep it here for logging/diagnostics.
      let message;
      if (data && typeof data === "object") {
        if (Array.isArray(data.name)) {
          message = data.name[0];
        } else if (Array.isArray(data.non_field_errors)) {
          message = data.non_field_errors[0];
        } else {
          message = data.message || data.detail || data.error;
        }
      }
      return {
        success: false,
        status: error?.response?.status,
        error: message || "Failed to create role",
      };
    }
  },

  // Update role permissions
  updateRolePermissions: async (roleId, permissionIds) => {
    try {
      const response = await api.post(
        `/users/roles/${roleId}/assign_permissions/`,
        {
          permission_ids: permissionIds,
        },
      );
      return {
        success: true,
        data: response.data,
        message:
          response.data.message || "Role permissions updated successfully",
      };
    } catch (error) {
      console.error("Error updating role permissions:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to update role permissions",
      };
    }
  },

  // getClubStaff / assignRoleToStaff / removeRoleFromStaff lived here until
  // this commit. The staff-assignment UI they served was removed from
  // RolesAndResponsibilities in 9e31e97 ("roles and resposnisibility options
  // removed"); the client half was left behind and had no caller anywhere in
  // src/ afterwards. removeRoleFromStaff posted to
  // /users/staff-roles/remove_role/, an action StaffRoleAssignmentViewSet has
  // never had (it knows assign_role, club_staff and transfer-club-admin) — a
  // guaranteed 404 that only never fired because nothing called it.
  // Note for searchers: clubService.getClubStaff (same file, used by
  // pages/search/ClubProfile.js) is a DIFFERENT function on a different
  // service and is untouched.

  // Get roles and permissions in one call (for efficiency)
  getRolesAndPermissions: async () => {
    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        api.get("/users/roles/"),
        api.get("/users/roles/available_permissions/"),
      ]);

      return {
        success: true,
        data: {
          roles: rolesResponse.data.results || rolesResponse.data,
          permissions:
            permissionsResponse.data.results || permissionsResponse.data,
        },
      };
    } catch (error) {
      console.error("Error fetching roles and permissions:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch roles and permissions",
      };
    }
  },
};

const kycService = {
  getVerificationLink: async () => {
    try {
      const response = await api.post(`/users/user-verification-link/`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error("Error fetching user verification link:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error.message ||
          "Failed to  user verification link",
      };
    }
  },

  getPublicPlayerVerificationLink: async (playerId, payload = {}) => {
    if (!playerId) {
      return {
        success: false,
        error: "Player ID is required to start verification",
      };
    }

    try {
      const response = await api.post(
        `/users/public/players/${playerId}/verification-link/`,
        payload,
      );

      // Handle both legacy DocuPass response and new biometric response
      const responseData = response.data?.results || response.data;
      const isBiometric = responseData?.verification_type === 'biometric' || responseData?.biometric_start_endpoint;

      return {
        success: response.data?.success !== false && (responseData?.success !== false),
        data: {
          ...responseData,
          // Preserve verification info if available
          verification: response.data?.verification,
        },
        message: responseData?.message || response.data?.message,
        // Include full results for backward compatibility
        results: responseData,
      };
    } catch (error) {
      console.error("Error fetching public player verification link:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.results?.message ||
          error.message ||
          "Failed to fetch verification link",
      };
    }
  },

  // Start biometric verification for a player
  startBiometricVerification: async (playerId) => {
    if (!playerId) {
      return {
        success: false,
        error: "Player ID is required",
      };
    }

    try {
      const response = await api.post(
        `/users/public/players/${playerId}/biometric-verification/start/`
      );
      return {
        success: response.data?.success !== false,
        data: response.data,
        profile_photo_url: response.data?.profile_photo_url,
        verify_endpoint: response.data?.verify_endpoint,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to start biometric verification",
        requires_photo_upload: error?.response?.data?.requires_photo_upload || false,
      };
    }
  },

  // Verify player identity with selfie (biometric)
  verifyBiometric: async (playerId, selfieData) => {
    if (!playerId) {
      return {
        success: false,
        error: "Player ID is required",
      };
    }

    try {
      const response = await api.post(
        `/users/public/players/${playerId}/biometric-verification/verify/`,
        selfieData
      );
      return {
        success: response.data?.success !== false,
        verified: response.data?.verified || false,
        confidence: response.data?.confidence,
        liveness: response.data?.liveness,
        message: response.data?.message,
        error: response.data?.error,
      };
    } catch (error) {
      return {
        success: false,
        verified: false,
        error: error?.response?.data?.message || error?.response?.data?.detail || "Failed to verify identity",
      };
    }
  },

  // Get biometric verification status
  getBiometricVerificationStatus: async (playerId) => {
    if (!playerId) {
      return {
        success: false,
        error: "Player ID is required",
      };
    }

    try {
      const response = await api.get(
        `/users/public/players/${playerId}/biometric-verification/status/`
      );
      return {
        success: response.data?.success !== false,
        data: response.data,
        verification_status: response.data?.verification_status,
        can_start_biometric: response.data?.can_start_biometric,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to get verification status",
      };
    }
  },
};

// ======================
// Organizer APIs
// ======================
const organizerService = {
  // Get organizers list with filters
  getOrganizers: async (params = {}) => {
    try {
      const response = await api.get("/users/organizer/registered-list/", {
        params,
        // The endpoint is IsPlatformStaff-gated (users/permissions.py:499-514),
        // so every product role gets a 403 here. The page turns that into an
        // explanatory state (QAF01-056); the global toast would only add noise.
        suppressPermissionToast: true,
      });
      return {
        success: true,
        data: response.data.results || [],
        message: response.data.message,
      };
    } catch (error) {
      console.error("Error fetching organizers:", error);
      return {
        success: false,
        // Same {success, error, status} shape the newer clients use
        // (messageRequestApi.js:55-62) so the caller can tell an access gate
        // apart from a real failure.
        status: error?.response?.status,
        error: error?.response?.data?.message || "Failed to fetch organizers",
      };
    }
  },

  // Initiate chat with organizer
  initiateOrganizerChat: async (organizerAccountId) => {
    try {
      const response = await api.post("/users/message/initiate/organizer/", {
        organizer_account_id: organizerAccountId,
      });
      return {
        success: true,
        data: response.data,
        channelId: response.data.channel_id,
        channelType: response.data.channel_type,
      };
    } catch (error) {
      console.error("Error initiating organizer chat:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to initiate chat",
      };
    }
  },
};

// ======================
// Lawyer APIs
// ======================
// Central mapping of directory filter keys -> backend query params for
// GET /users/lawyer/registered-list. Structured filters are applied
// client-side today (see hooks/lawyer/useLawyers.js); sending them here is
// harmless and forward-compatible once BE-2 wires server-side filtering.
// Keep param names in this ONE place so a server-side switch is a no-op in
// the hook.
const LAWYER_QUERY_PARAMS = {
  search: "search",
  jurisdiction: "jurisdiction",
  specialization: "specialization",
  language: "language",
  city: "city",
  verified: "verified",
};

const buildLawyerParams = (filters = {}) => {
  const params = {};
  Object.entries(LAWYER_QUERY_PARAMS).forEach(([key, param]) => {
    const value = filters[key];
    if (value !== undefined && value !== null && value !== "") {
      params[param] = value;
    }
  });
  return params;
};

// Whitelisted lawyer-profile fields the settings form may PATCH. Matches the
// SCHEMA-PLAN / BE-2 field names. `is_verified` is intentionally NOT here — it
// is admin-only and read-only via the profile endpoint.
const LAWYER_PROFILE_FIELDS = [
  "specializations",
  "jurisdictions",
  "languages",
  "firm",
  "bio",
  "city",
  "country",
  "license_number",
  "website_url",
  "linkedin_url",
  "contact_email_public",
  "contact_phone_public",
];

const lawyerService = {
  // Get lawyers list. Accepts a filters object (search/jurisdiction/
  // specialization/language/city/verified); param names are centralized in
  // LAWYER_QUERY_PARAMS.
  getLawyers: async (filters = {}, signal) => {
    try {
      const response = await api.get("/users/lawyer/registered-list", {
        params: buildLawyerParams(filters),
        signal,
      });
      return {
        success: true,
        data: response.data.data?.results || response.data.results || [],
        message: response.data.message,
      };
    } catch (error) {
      if (error.name === "CanceledError") {
        throw error;
      }
      console.error("Error fetching lawyers:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch lawyers",
      };
    }
  },

  // Fetch the signed-in user's own lawyer profile (owner sees own contact +
  // toggles). BE-2: GET /users/lawyer/profile/.
  getMyProfile: async (signal) => {
    try {
      const response = await api.get("/users/lawyer/profile/", { signal });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      if (error.name === "CanceledError") throw error;
      return {
        success: false,
        status: error?.response?.status,
        error:
          error?.response?.data?.message || "Failed to load lawyer profile",
      };
    }
  },

  // Update the signed-in user's lawyer profile. Only whitelisted fields are
  // sent (see LAWYER_PROFILE_FIELDS); `is_verified` is admin-only server-side.
  updateMyProfile: async (payload = {}) => {
    try {
      const body = {};
      LAWYER_PROFILE_FIELDS.forEach((field) => {
        if (payload[field] !== undefined) body[field] = payload[field];
      });
      const response = await api.patch("/users/lawyer/profile/", body);
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      return {
        success: false,
        status: error?.response?.status,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to save lawyer profile",
      };
    }
  },

  // Full lawyer profile for the detail view (license_number lives here, not on
  // the grid card). BE-2: GET /users/lawyer/<pk>/ (LawyerDetailSerializer).
  getDetail: async (lawyerId, signal) => {
    try {
      const response = await api.get(`/users/lawyer/${lawyerId}/`, { signal });
      return { success: true, data: response.data?.data || response.data };
    } catch (error) {
      if (error.name === "CanceledError") throw error;
      return {
        success: false,
        status: error?.response?.status,
        error:
          error?.response?.data?.message || "Failed to load lawyer profile",
      };
    }
  },

  // Start a chat/request with a lawyer (chat-first contact). BE-3, 1:1 the
  // organizer pattern: POST /users/message/initiate/lawyer/ with the lawyer's
  // account id + optional topic/message → { channel_id, channel_type }.
  initiateChat: async ({ lawyerAccountId, topic, message } = {}) => {
    try {
      const body = { lawyer_account_id: lawyerAccountId };
      if (topic) body.topic = topic;
      if (message) body.message = message;
      const response = await api.post("/users/message/initiate/lawyer/", body);
      return {
        success: true,
        data: response.data,
        channelId: response.data?.channel_id,
        channelType: response.data?.channel_type,
      };
    } catch (error) {
      return {
        success: false,
        status: error?.response?.status,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to start chat with lawyer",
      };
    }
  },

  // ---- D-010: Contact-Lawyer as an ACCEPT/DECLINE request (not an instant
  // chat). Endpoint URLs are centralized here; confirm exact paths with BE and
  // change in ONE place if they differ. All methods are defensive (404/other
  // → success:false so the UI degrades to its normal state).
  //
  // Create the request. Same endpoint as the old initiate (per D-010), but the
  // response is now { request_id, status:"pending" } instead of a channel.
  createRequest: async ({ lawyerAccountId, topic, message } = {}) => {
    try {
      const body = { lawyer_account_id: lawyerAccountId };
      if (topic) body.topic = topic;
      if (message) body.message = message;
      const response = await api.post("/users/message/initiate/lawyer/", body);
      const d = response.data || {};
      return {
        success: true,
        data: d,
        requestId: d.request_id ?? d.id,
        status: d.status || "pending",
      };
    } catch (error) {
      return {
        success: false,
        status: error?.response?.status,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to send request",
      };
    }
  },

  // Incoming requests for the signed-in lawyer (to accept/decline).
  getIncomingRequests: async (signal) => {
    try {
      const response = await api.get("/users/lawyer/requests/incoming/", { signal });
      const d = response.data;
      const list = d?.data?.results || d?.results || d?.data || d || [];
      return { success: true, data: Array.isArray(list) ? list : [] };
    } catch (error) {
      if (error.name === "CanceledError") throw error;
      return { success: false, status: error?.response?.status, error: error?.response?.data?.message || "Failed to load requests" };
    }
  },

  // Outgoing requests the signed-in user has sent (to show "Request pending").
  getOutgoingRequests: async (signal) => {
    try {
      const response = await api.get("/users/lawyer/requests/outgoing/", { signal });
      const d = response.data;
      const list = d?.data?.results || d?.results || d?.data || d || [];
      return { success: true, data: Array.isArray(list) ? list : [] };
    } catch (error) {
      if (error.name === "CanceledError") throw error;
      return { success: false, status: error?.response?.status, error: error?.response?.data?.message || "Failed to load requests" };
    }
  },

  // Lawyer accepts → the GetStream channel is created; returns it so the caller
  // can open it in the inbox. BE contract: POST .../requests/accept/ with the
  // request_id in the BODY (not the path).
  acceptRequest: async (requestId) => {
    try {
      const response = await api.post("/users/lawyer/requests/accept/", { request_id: requestId });
      return {
        success: true,
        data: response.data,
        channelId: response.data?.channel_id,
        channelType: response.data?.channel_type,
      };
    } catch (error) {
      return { success: false, status: error?.response?.status, error: error?.response?.data?.message || error?.response?.data?.detail || "Failed to accept request" };
    }
  },

  // Lawyer declines → no chat; requester may re-request later. BE contract:
  // POST .../requests/decline/ with the request_id in the BODY.
  declineRequest: async (requestId) => {
    try {
      const response = await api.post("/users/lawyer/requests/decline/", { request_id: requestId });
      return { success: true, data: response.data, status: response.data?.status || "declined" };
    } catch (error) {
      return { success: false, status: error?.response?.status, error: error?.response?.data?.message || error?.response?.data?.detail || "Failed to decline request" };
    }
  },
};

// ======================
// Live Market APIs
// ======================
const liveMarketService = {
  // Get live market players
  getLiveMarketPlayers: async (params = {}) => {
    try {
      const response = await api.get("/dashboard/live-market/", {
        params,
      });
      return {
        success: true,
        data: response.data.results || [],
        pagination: {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous,
        },
      };
    } catch (error) {
      console.error("Error fetching live market players:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to fetch live market players",
      };
    }
  },

  // Contact other club (Club ↔ Club Chat)
  createClubChat: async (clubId) => {
    try {
      const response = await api.post("/dashboard/chat/create_club_chat/", {
        club_id: clubId,
      });
      return {
        success: true,
        data: response.data,
        channelId: response.data.channel_id,
        channelType: response.data.channel_type,
      };
    } catch (error) {
      console.error("Error creating club chat:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.non_field_errors ||
          "Failed to create club chat",
      };
    }
  },
};

// Agent-side staff roster (TP-MyStaff P1). Mirrors the {success,data,error}
// shape of playerService.getPlayers. Endpoint is re-pointed at the
// invitation-derived roster by the paired backend package.
const agentStaffService = {
  getStaffList: async (params, signal) => {
    try {
      const response = await api.get("/users/agents/staff-list/", {
        params,
        signal,
      });
      const raw = response?.data;
      // Tolerate both a paginated {results:[...]} envelope and a bare array.
      const results = Array.isArray(raw) ? raw : raw?.results || [];
      return {
        success: true,
        data: results.map((s) => ({ ...s, id: s.id || s.account_id })),
      };
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        throw error;
      }
      console.error("Error in getStaffList:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch staff",
      };
    }
  },
};

export {
  dashboardService,
  searchService,
  authService,
  trustedDeviceService,
  bannedAgentService,
  userService,
  tenderService,
  playerService,
  agentStaffService,
  clubService,
  documentService,
  translationService,
  freeAgentsService,
  transfermarktService,
  mandateInvitationService,
  staffInvitationService,
  coachInvitationService,
  agentInvitationService,
  clubPlayerInvitationService,
  sentInvitationsService,
  notificationService,
  notificationPreferenceService,
  contactService,
  businessCardService,
  streamTokenService,
  meetingService,
  transferWindowService,
  transferPointsService,
  agentService,
  streamExportService,
  chatActionService,
  chatTimerService,
  chatCallOutcomeService,
  chatExportService,
  contactSupportService,
  rolesPermissionsService,
  kycService,
  organizerService,
  lawyerService,
  liveMarketService,
  api as default,
};

// Export shareService after its definition (moved below)

// Expose utility to listen to api idle events (optional)
export const onApiIdle = (handler) => {
  window.addEventListener(API_IDLE_EVENT, handler);
  return () => window.removeEventListener(API_IDLE_EVENT, handler);
};

// Agent ↔ Player Chat API
export const initiateAgentPlayerChat = async (
  playerAccountId,
  isClaimRepresentation = false,
) => {
  try {
    const response = await api.post("/users/message/initiate/agent-player/", {
      player_account_id: playerAccountId,
      is_claim_representation: isClaimRepresentation,
    });
    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error initiating agent-player chat:", error);
    return {
      success: false,
      error:
        error.response?.data?.detail ||
        error.message ||
        "Failed to initiate chat",
    };
  }
};

// Mandate Invite Response API (for players)
export const respondToMandateInvite = async (inviteId, action) => {
  try {
    const response = await api.patch(
      `/users/mandate-invites/${inviteId}/respond/`,
      {
        action: action, // "accept" or "decline"
      },
    );
    return { success: true, data: response.data };
  } catch (error) {
    console.error("Error responding to mandate invite:", error);
    return {
      success: false,
      error:
        error.response?.data?.detail ||
        error.message ||
        "Failed to respond to invite",
    };
  }
};

// Share API Functions
const shareService = {
  // Get list of recipients for sharing
  getRecipients: async (query = "", page = 1, perPage = 10) => {
    try {
      const params = new URLSearchParams();
      if (query) params.append("q", query);
      if (page) params.append("page", page);
      if (perPage) params.append("per_page", perPage);

      const response = await api.get(
        `/dashboard/chat/recipients/?${params.toString()}`,
      );
      return {
        success: true,
        data: response.data.results,
        pagination: response.data.pagination,
      };
    } catch (error) {
      console.error("Error fetching share recipients:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch recipients",
      };
    }
  },

  // D27 role-scoped recipient search (be-core db37e7e). Cross-party discovery for
  // the "+" picker's VIA-REQUEST section: cross-agency agents + clubs for an
  // agent caller (reachable_via "request"), agents for a club caller
  // (reachable_via "direct"), empty for DM-only roles. Public identity fields
  // only; the send still goes through the message-request gate + D12 cap.
  recipientSearch: async (query = "", limit = 20) => {
    try {
      const params = new URLSearchParams();
      if (query) params.append("q", query);
      params.append("limit", String(limit));
      const response = await api.get(
        `/dashboard/chat/recipient-search/?${params.toString()}`,
      );
      return {
        success: true,
        roleScope: response.data?.role_scope || null,
        data: Array.isArray(response.data?.results) ? response.data.results : [],
      };
    } catch (error) {
      console.error("Error searching recipients:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to search recipients",
        roleScope: null,
        data: [],
      };
    }
  },

  // Share single item with one recipient
  shareSingle: async (target, shareType, url, data, messageText = "") => {
    try {
      const payload = {
        target,
        share_type: shareType,
        url,
        data,
        message_text: messageText,
      };

      const response = await api.post("/dashboard/chat/share/", payload);
      return {
        success: true,
        data: response.data,
        channelId: response.data.channel_id,
        messageId: response.data.message_id,
      };
    } catch (error) {
      console.error("Error sharing single item:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to share item",
      };
    }
  },

  // Share bulk items with multiple recipients
  shareBulk: async (recipients, shareType, url, data, messageText = "") => {
    try {
      const payload = {
        recipients,
        share_type: shareType,
        url,
        data,
        message_text: messageText,
      };

      const response = await api.post("/dashboard/chat/share/bulk/", payload);
      return {
        success: true,
        data: response.data.results,
        failures: response.data.failures,
      };
    } catch (error) {
      console.error("Error sharing bulk items:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to share items",
      };
    }
  },

  // Trigger message notification
  notifyMessage: async (recipientAccountId, messageText, channelId = null) => {
    try {
      const payload = {
        recipient_account_id: recipientAccountId,
        message_text: messageText,
      };

      if (channelId) {
        payload.channel_id = channelId;
      }

      const response = await api.post(
        "/dashboard/chat/notify-message/",
        payload,
      );
      return {
        success: true,
        data: response.data,
        notificationId: response.data.notification_id,
      };
    } catch (error) {
      console.error("Error triggering message notification:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to trigger notification",
      };
    }
  },
};

export { shareService };

// Public Staff Profile API
export const getPublicStaffProfile = async (accountId) => {
  try {
    const response = await api.get(`/users/public/profile/staff/${accountId}/`);
    return { success: true, data: response.data };
  } catch (error) {
    return {
      success: false,
      error: error.response?.data?.message || "Failed to fetch staff profile",
    };
  }
};

// ======================
// Player Mandate APIs
// ======================
export const mandateService = {
  // Get mandate details by token
  getMandateDetailsByToken: async (token) => {
    try {
      const response = await api.get(`/users/public/mandate-invites/${token}/`);
      return {
        success: response.data?.success !== false,
        data: response.data?.results || response.data,
        message: response.data?.message,
      };
    } catch (error) {
      console.error("Error fetching mandate details:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch representation details",
      };
    }
  },

  // Send OTP to the invite-associated mobile for mandate verification
  sendMandateOtp: async (token, mobile = null) => {
    try {
      const payload = mobile ? { mobile } : {};
      const response = await api.post(
        `/users/public/mandate-invites/${token}/start/`,
        payload,
      );
      return {
        success: response.data?.success !== false,
        data: response.data?.results || response.data,
        message: response.data?.message || "OTP sent successfully",
      };
    } catch (error) {
      console.error("Error sending mandate OTP:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to send OTP",
      };
    }
  },

  // Respond to mandate (accept/decline) by token
  respondToMandate: async (token, action, mobile, otp, birthDate) => {
    try {
      const payload = { action };
      if (mobile && otp) {
        payload.mobile = mobile;
        payload.otp = otp;
      }
      if (birthDate) {
        payload.birth_date = birthDate;
      }

      const response = await api.patch(
        `/users/public/mandate-invites/${token}/respond/`,
        payload,
      );
      return {
        success: response.data?.success !== false,
        data: response.data?.results || response.data,
        message: response.data?.message || `Representation ${action}ed successfully`,
      };
    } catch (error) {
      console.error("Error responding to mandate:", error);
      return {
        success: false,
        error: error?.response?.data?.message || `Failed to ${action} representation`,
        code: error?.response?.data?.code,
      };
    }
  },

  // Renew mandate via invite (manual)
  renewMandateInvite: async (mandateId, body) => {
    try {
      const response = await api.post(
        `/users/mandates/${mandateId}/renew-invite/`,
        body,
      );
      return {
        success: response.data?.success !== false,
        data: response.data?.results || response.data,
        message: response.data?.message || "Renew invite sent successfully",
      };
    } catch (error) {
      console.error("Error renewing mandate invite:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to renew representation invite",
      };
    }
  },

  // Get agency agents dropdown
  getAgencyAgentsDropdown: async () => {
    try {
      const response = await api.get("/users/agency/agents/dropdown");
      // Normalize response shape to always return an array in `data`
      const results = response?.data?.results;
      let list = [];
      if (Array.isArray(results)) {
        list = results;
      } else if (Array.isArray(results?.data)) {
        list = results.data;
      } else if (Array.isArray(response?.data?.data)) {
        list = response.data.data;
      } else if (Array.isArray(response?.data)) {
        list = response.data;
      }

      return {
        success: response.data?.success !== false,
        data: list,
        message: response.data?.message,
      };
    } catch (error) {
      console.error("Error fetching agency agents dropdown:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch agents",
      };
    }
  },
};

// ======================
// Staff/Coach Mandate APIs (TP-Coach-Mandate)
// ======================
// Cloned 1:1 from mandateInvitationService / mandateService (player template),
// re-pointed at the coach/staff-mandate endpoint family. Same normalized
// {success, data, error} pattern — service never throws.
export const staffMandateService = {
  // Send mandate invitation for a coach (TheSports lane, ts_coach_id) or an
  // existing platform account (internal lane). BE contract: the external lane
  // is POST /users/coaches/<ts_coach_id>/mandate-invites/ (path id = TSCoach
  // id); the internal lane is POST /users/staff-mandate-invites/ with
  // `account_id` (+ optional `kind`) in the body — the path slot must NOT
  // carry the account id (the backend treats any path id as a TSCoach id).
  sendMandateInvitation: async (subjectId, data) => {
    try {
      const response = data?.account_id
        ? await api.post(`/users/staff-mandate-invites/`, data)
        : await api.post(`/users/coaches/${subjectId}/mandate-invites/`, data);
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error sending staff mandate invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || error?.response?.data?.detail || "Failed to send representation invitation",
        requires_photo_upload: error?.response?.data?.requires_photo_upload || false,
      };
    }
  },

  // Check if a TheSports coach has a usable reference photo (for agents)
  checkCoachPhoto: async (tsCoachId) => {
    try {
      const response = await api.get(
        `/users/agents/ts-coaches/${tsCoachId}/check-photo/`
      );
      return {
        success: response.data?.success !== false,
        has_photo: response.data?.has_photo || false,
        photo_url: response.data?.photo_url || null,
        photo_source: response.data?.photo_source || null,
      };
    } catch (error) {
      return {
        success: false,
        has_photo: false,
        error: error?.response?.data?.message || error?.response?.data?.detail || "Failed to check coach photo",
      };
    }
  },

  // Upload a reference photo for a TheSports coach (for agents)
  uploadCoachPhoto: async (tsCoachId, photoFile) => {
    try {
      const formData = new FormData();
      formData.append('photo', photoFile);

      const response = await api.post(
        `/users/agents/ts-coaches/${tsCoachId}/upload-photo/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return {
        success: response.data?.success !== false,
        photo_url: response.data?.photo_url || null,
        photo_source: response.data?.photo_source || null,
        message: response.data?.message || "Photo uploaded successfully",
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || error?.response?.data?.detail || "Failed to upload photo",
      };
    }
  },

  // Internal lane: search existing platform coach/staff accounts.
  // Response: {results:[{id, kind:'staff'|'coach', account_id, name, photo, role, club_name}]}
  searchStaffAccounts: async (query, signal) => {
    try {
      const response = await api.get("/users/agents/staff-accounts/search/", {
        params: { q: query },
        signal,
      });
      const results = Array.isArray(response.data?.results)
        ? response.data.results
        : [];
      return { success: true, data: results };
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError") {
        throw error;
      }
      console.error("Staff account search error:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Search failed",
        data: [],
      };
    }
  },

  // Public: get staff-mandate details by invite token
  getMandateDetailsByToken: async (token) => {
    try {
      const response = await api.get(`/users/public/staff-mandate-invites/${token}/`);
      return {
        success: response.data?.success !== false,
        data: response.data?.results || response.data,
        message: response.data?.message,
      };
    } catch (error) {
      console.error("Error fetching staff mandate details:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to fetch representation details",
      };
    }
  },

  // Public: respond to staff mandate (accept/decline) by token.
  // 400 {code:'ID_REQUIRED'} bounces the caller back to verification.
  // (No birth_date leg — the staff chain never requests one; F8.)
  respondToMandate: async (token, action) => {
    try {
      const response = await api.patch(
        `/users/public/staff-mandate-invites/${token}/respond/`,
        { action },
      );
      return {
        success: response.data?.success !== false,
        data: response.data?.results || response.data,
        message: response.data?.message || `Representation ${action}ed successfully`,
      };
    } catch (error) {
      console.error("Error responding to staff mandate:", error);
      return {
        success: false,
        error: error?.response?.data?.message || `Failed to ${action} representation`,
        code: error?.response?.data?.code,
      };
    }
  },

  // In-app lane (F2): the logged-in caller's OWN pending staff-mandate
  // invitations — GET /users/staff-mandate-invites/pending/ returns a bare
  // list (mirror of the player /users/player-invites/list/ shape).
  getPendingMandateInvitations: async () => {
    try {
      const response = await api.get(
        "/users/staff-mandate-invites/pending/?status=pending",
      );
      return {
        success: true,
        data: Array.isArray(response.data) ? response.data : [],
      };
    } catch (error) {
      console.error("Error fetching pending staff mandate invitations:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to fetch pending invitations",
        data: [],
      };
    }
  },

  // In-app lane: existing platform accounts respond while logged in (Q1:
  // no re-biometric for existing accounts — D-006).
  respondToMandateInvitation: async (inviteId, action) => {
    try {
      const response = await api.patch(
        `/users/staff-mandate-invites/${inviteId}/respond/`,
        {
          action,
        },
      );
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error responding to staff mandate invitation:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          "Failed to respond to representation invitation",
      };
    }
  },

  // Extend staff mandate duration
  extendMandate: async (mandateId, endDate) => {
    try {
      const response = await api.patch(`/users/staff-mandates/${mandateId}/extend/`, {
        end_date: endDate,
      });
      return {
        success: true,
        data: response.data.results || response.data,
      };
    } catch (error) {
      console.error("Error extending staff mandate:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to extend representation",
      };
    }
  },

  // Renew staff mandate via invite (manual)
  renewMandateInvite: async (mandateId, body) => {
    try {
      const response = await api.post(
        `/users/staff-mandates/${mandateId}/renew-invite/`,
        body,
      );
      return {
        success: response.data?.success !== false,
        data: response.data?.results || response.data,
        message: response.data?.message || "Renew invite sent successfully",
      };
    } catch (error) {
      console.error("Error renewing staff mandate invite:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message || "Failed to renew representation invite",
      };
    }
  },

  // AgencyAdmin staff board — mirrors /users/agency/players/board shape
  // (same keys; staff/coach entries carry managing_agent + mandate fields).
  getAgencyStaffBoard: async (params = {}) => {
    try {
      const response = await api.get("/users/agency/staff/board", { params });
      const raw = response?.data;
      const results = Array.isArray(raw)
        ? raw
        : raw?.results?.data || raw?.results || raw?.data || [];
      return {
        success: true,
        data: Array.isArray(results) ? results : [],
      };
    } catch (error) {
      console.error("Error fetching agency staff board:", error);
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to fetch staff",
        data: [],
      };
    }
  },

  // Transfer a coach/staff mandate to another agent of the agency.
  // BE contract: POST /users/agency/staff/<member_id>/reassign-agent/ where
  // <member_id> is the Staff/Coach PK (the staff board row `id`), and the
  // body REQUIRES {kind: 'staff'|'coach', new_agent_account_id}. Response
  // payload lives under `data` (not `results`).
  reassignStaffAgent: async (staffId, newAgentAccountId, kind) => {
    try {
      const response = await api.post(
        `/users/agency/staff/${staffId}/reassign-agent/`,
        { kind, new_agent_account_id: newAgentAccountId },
      );
      return {
        success: response.data?.success !== false,
        data: response.data?.data || response.data,
        message: response.data?.message || "Staff member reassigned successfully",
      };
    } catch (error) {
      console.error("Error reassigning staff agent:", error);
      return {
        success: false,
        error:
          error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to reassign staff member",
      };
    }
  },
};

// Staff/coach biometric KYC — cloned from kycService (player template), same
// shapes, coach endpoint namespace. Reference photo = coach photo.
export const staffKycService = {
  // Start biometric verification for a coach/staff member. The optional
  // invite token binds the chain to a concrete staff-mandate invitation
  // (F6 token binding — BE refuses tokens for another coach).
  startBiometricVerification: async (tsCoachId, token) => {
    if (!tsCoachId) {
      return {
        success: false,
        error: "Coach ID is required",
      };
    }

    try {
      const response = await api.post(
        `/users/public/coaches/${tsCoachId}/biometric-verification/start/`,
        token ? { token } : {}
      );
      return {
        success: response.data?.success !== false,
        data: response.data,
        profile_photo_url: response.data?.profile_photo_url,
        verify_endpoint: response.data?.verify_endpoint,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to start biometric verification",
        requires_photo_upload: error?.response?.data?.requires_photo_upload || false,
      };
    }
  },

  // Verify coach/staff identity with selfie (biometric). Optional token —
  // when present the BE resolves/stamps the verification via THAT
  // invitation's mandate (F6 token binding).
  verifyBiometric: async (tsCoachId, selfieData, token) => {
    if (!tsCoachId) {
      return {
        success: false,
        error: "Coach ID is required",
      };
    }

    try {
      const response = await api.post(
        `/users/public/coaches/${tsCoachId}/biometric-verification/verify/`,
        token ? { ...selfieData, token } : selfieData
      );
      return {
        success: response.data?.success !== false,
        verified: response.data?.verified || false,
        confidence: response.data?.confidence,
        liveness: response.data?.liveness,
        message: response.data?.message,
        error: response.data?.error,
      };
    } catch (error) {
      return {
        success: false,
        verified: false,
        error: error?.response?.data?.message || error?.response?.data?.detail || "Failed to verify identity",
      };
    }
  },

  // Get biometric verification status for a coach/staff member
  getBiometricVerificationStatus: async (tsCoachId) => {
    if (!tsCoachId) {
      return {
        success: false,
        error: "Coach ID is required",
      };
    }

    try {
      const response = await api.get(
        `/users/public/coaches/${tsCoachId}/biometric-verification/status/`
      );
      return {
        success: response.data?.success !== false,
        data: response.data,
        verification_status: response.data?.verification_status,
        can_start_biometric: response.data?.can_start_biometric,
      };
    } catch (error) {
      return {
        success: false,
        error: error?.response?.data?.message || "Failed to get verification status",
      };
    }
  },
};
